import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { 
  Activity,
  AlertTriangle,
  CalendarClock,
  CircleAlert,
  CircleCheckBig,
  ClipboardList,
  Layers,
  MinusCircle,
  Package,
  PlusCircle, 
  RefreshCw,
  Truck,
  Upload,
  X,
  FileText,
  Download,
  Eye,
  Trash2
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import './InventoryManagement.css';

const API_BASE = '/api/inventory/v2';
const UNIT_OPTIONS = ['kg', 'MT', 'pcs', 'l', 'boxes', 'tests', 'litres', '1 kg tins', '25kg bags'];

const DEFAULT_BACKEND_BASE =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://intranet.nesimltd.co.ke'
    : 'http://localhost:5000');

const UPLOAD_API_BASE = DEFAULT_BACKEND_BASE;
const MAX_DIRECT_UPLOAD_SIZE = 4 * 1024 * 1024; // 4MB
const UPLOAD_CHUNK_SIZE = 512 * 1024; // 512KB chunks

const getAuthHeaders = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  const token = window.localStorage ? window.localStorage.getItem('token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const mapUploadedFileResponse = (uploadedFile) => {
  if (!uploadedFile) return null;
  return {
    filename: uploadedFile.filename,
    originalName: uploadedFile.originalName || uploadedFile.name,
    url: uploadedFile.url || uploadedFile.path,
    path: uploadedFile.path || uploadedFile.url,
    size: uploadedFile.size,
    mimetype: uploadedFile.mimetype,
    uploadedAt: new Date()
  };
};

const uploadViaStandardEndpoint = async (files, authHeaders, context) => {
  if (!files || files.length === 0) {
    return [];
  }

  const formData = new FormData();
  if (context) {
    formData.append('context', context);
  }
  files.forEach(file => formData.append('files', file));

  const response = await fetch(`${UPLOAD_API_BASE}/api/upload/chat`, {
    method: 'POST',
    headers: authHeaders,
    body: formData,
    credentials: 'include'
  });

  if (!response.ok) {
    let errorText = '';
    try {
      // Clone the response to read it without consuming the stream
      const responseClone = response.clone();
      try {
        const errorBody = await responseClone.json();
        errorText = errorBody.message || errorBody.error || JSON.stringify(errorBody);
        
        // Handle rate limit errors specifically
        if (response.status === 429) {
          const rateLimitMessage = errorBody.message || 'Too many requests. Please wait a few minutes before trying again.';
          throw new Error(`Upload rate limit exceeded: ${rateLimitMessage}. Please wait before uploading again.`);
        }
      } catch (jsonError) {
        // If JSON parsing fails, try text
        const textResponse = response.clone();
        errorText = await textResponse.text().catch(() => 'Unknown error');
      }
    } catch (parseError) {
      errorText = `HTTP ${response.status}: ${parseError.message || 'Unknown error'}`;
    }
    throw new Error(`Upload failed: ${response.status} - ${errorText}`);
  }

  const result = await response.json();
  if (!result.files || result.files.length === 0) {
    throw new Error('No files returned from upload');
  }

  return result.files
    .map(mapUploadedFileResponse)
    .filter(Boolean);
};

const uploadFileViaChunks = async (file, context, authHeaders) => {
  const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const totalChunks = Math.max(1, Math.ceil(file.size / UPLOAD_CHUNK_SIZE));

  // Helper function to upload a chunk with retry logic
  const uploadChunkWithRetry = async (chunkForm, chunkIndex, maxRetries = 3) => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(`${UPLOAD_API_BASE}/api/upload/chunk`, {
          method: 'POST',
          headers: authHeaders,
          body: chunkForm,
          credentials: 'include'
        });

        if (!response.ok) {
          let errorText = '';
          let isRateLimit = false;
          try {
            // Clone the response to read it without consuming the stream
            const responseClone = response.clone();
            try {
              const errorBody = await responseClone.json();
              errorText = errorBody.message || errorBody.error || JSON.stringify(errorBody);
              
              // Handle rate limit errors specifically
              if (response.status === 429) {
                isRateLimit = true;
                const rateLimitMessage = errorBody.message || 'Too many requests. Please wait a few minutes before trying again.';
                
                // If this is not the last retry, wait with exponential backoff
                if (attempt < maxRetries - 1) {
                  const waitTime = Math.min(30000 * Math.pow(2, attempt), 300000); // Max 5 minutes
                  console.log(`Rate limit hit, waiting ${waitTime / 1000} seconds before retry ${attempt + 2}/${maxRetries}...`);
                  await new Promise(resolve => setTimeout(resolve, waitTime));
                  continue; // Retry the upload
                } else {
                  // Last attempt failed, throw error
                  throw new Error(`Upload rate limit exceeded: ${rateLimitMessage}. Please wait 5 minutes before trying again.`);
                }
              }
            } catch (jsonError) {
              // If JSON parsing fails, try text
              const textResponse = response.clone();
              errorText = await textResponse.text().catch(() => 'Unknown error');
            }
          } catch (parseError) {
            // Last resort - just use status
            errorText = `HTTP ${response.status}: ${parseError.message || 'Unknown error'}`;
          }
          
          // If it's not a rate limit or we've exhausted retries, throw error
          if (!isRateLimit || attempt === maxRetries - 1) {
            throw new Error(`Chunk upload failed (chunk ${chunkIndex + 1}/${totalChunks}): ${response.status} - ${errorText}`);
          }
        } else {
          // Success - return
          return;
        }
      } catch (error) {
        // If it's the last attempt or not a rate limit error, throw
        if (attempt === maxRetries - 1 || !error.message.includes('rate limit')) {
          throw error;
        }
        // Otherwise, wait and retry
        const waitTime = Math.min(5000 * Math.pow(2, attempt), 60000); // Max 1 minute
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
  };

  for (let index = 0; index < totalChunks; index += 1) {
    const start = index * UPLOAD_CHUNK_SIZE;
    const end = Math.min(file.size, start + UPLOAD_CHUNK_SIZE);
    const chunk = file.slice(start, end);

    const chunkForm = new FormData();
    chunkForm.append('uploadId', uploadId);
    chunkForm.append('chunkIndex', index);
    chunkForm.append('totalChunks', totalChunks);
    chunkForm.append('filename', file.name);
    chunkForm.append('mimetype', file.type || 'application/octet-stream');
    chunkForm.append('context', context);
    chunkForm.append('chunk', chunk, file.name);

    // Upload chunk with retry logic
    await uploadChunkWithRetry(chunkForm, index);

    // Add a small delay between chunks to avoid hitting rate limits
    // Only delay if not the last chunk
    if (index < totalChunks - 1) {
      await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay between chunks
    }
  }

  const finalizeHeaders = {
    'Content-Type': 'application/json',
    ...authHeaders
  };

  const finalizeResponse = await fetch(`${UPLOAD_API_BASE}/api/upload/chunk/complete`, {
    method: 'POST',
    headers: finalizeHeaders,
    body: JSON.stringify({
      uploadId,
      filename: file.name,
      mimetype: file.type || 'application/octet-stream',
      totalChunks,
      context
    }),
    credentials: 'include'
  });

  if (!finalizeResponse.ok) {
    const errorText = await finalizeResponse.text();
    throw new Error(`Failed to finalize upload: ${finalizeResponse.status} - ${errorText}`);
  }

  const finalizeResult = await finalizeResponse.json();
  if (!finalizeResult.files || finalizeResult.files.length === 0) {
    throw new Error('No files returned after chunked upload completion');
  }

  const mappedFile = mapUploadedFileResponse(finalizeResult.files[0]);
  if (!mappedFile) {
    throw new Error('Unable to map uploaded file response');
  }
  return mappedFile;
};

const uploadFilesWithAutoChunking = async ({ files, context = 'documents' }) => {
  const fileArray = Array.from(files || []);
  if (fileArray.length === 0) {
    return [];
  }

  const authHeaders = getAuthHeaders();

  const regularFiles = [];
  const largeFiles = [];

  fileArray.forEach((file, index) => {
    if (file.size > MAX_DIRECT_UPLOAD_SIZE) {
      largeFiles.push({ file, index });
    } else {
      regularFiles.push({ file, index });
    }
  });

  const uploadedFiles = new Array(fileArray.length);

  if (regularFiles.length > 0) {
    const mapped = await uploadViaStandardEndpoint(
      regularFiles.map(item => item.file),
      authHeaders,
      context
    );
    mapped.forEach((fileData, idx) => {
      const targetIndex = regularFiles[idx].index;
      uploadedFiles[targetIndex] = fileData;
    });
  }

  for (const largeFileEntry of largeFiles) {
    const chunkedFile = await uploadFileViaChunks(largeFileEntry.file, context, authHeaders);
    uploadedFiles[largeFileEntry.index] = chunkedFile;
  }

  return uploadedFiles.filter(Boolean);
};

// File viewing and downloading utilities (matching WorkflowAutomations pattern)
const ensureAbsoluteUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  return `${UPLOAD_API_BASE}${url.startsWith('/') ? url : `/${url}`}`;
};

const parseFilenameFromDisposition = (headerValue) => {
  if (!headerValue) return null;
  const match = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(headerValue);
  if (!match || !match[1]) return null;
  try {
    return decodeURIComponent(match[1].replace(/\"/g, '').trim());
  } catch (error) {
    console.warn('Failed to decode filename from header:', error);
    return match[1].replace(/\"/g, '').trim();
  }
};

const resolveFileDownloadInfo = (file) => {
  if (!file) {
    return { url: null, filename: null };
  }

  const url =
    file.downloadUrl ||
    file.url ||
    file.path ||
    file.filePath ||
    (file.location ? file.location : null) ||
    (file.id ? `/api/files/${file.id}` : null) ||
    (file._id ? `/uploads/chat/${file._id}` : null) ||
    (file.filename ? `/uploads/chat/${file.filename}` : null);

  const filename =
    file.name ||
    file.originalName ||
    file.filename ||
    file.fileName ||
    file.label ||
    'download';

  return { url, filename };
};

const downloadInventoryFile = async (file) => {
  const { url, filename } = resolveFileDownloadInfo(file);

  if (!url) {
    throw new Error('No valid file URL found.');
  }

  const absoluteUrl = ensureAbsoluteUrl(url);
  if (!absoluteUrl) {
    throw new Error('Unable to construct download URL.');
  }

  try {
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(absoluteUrl, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Download failed with status ${response.status}`);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    const derivedFilename =
      parseFilenameFromDisposition(contentDisposition) || filename;
    const downloadUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', derivedFilename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      window.URL.revokeObjectURL(downloadUrl);
    }, 60 * 1000);
  } catch (error) {
    console.error('❌ [FILE_DOWNLOAD] Failed to download file', {
      file,
      absoluteUrl,
      error,
    });
    throw error;
  }
};

const tabs = [
  { id: 'dashboard', label: 'Overview', icon: Activity },
  { id: 'current-stock', label: 'Current Stock', icon: Package },
  { id: 'batches', label: 'Batches', icon: Layers },
  { id: 'movements', label: 'Stock Movements', icon: RefreshCw },
  { id: 'incoming-orders', label: 'Incoming Orders', icon: Truck }
];

const MOVEMENT_TYPES = {
  RECEIPT: { label: 'Receipt', tone: 'positive' },
  ISSUE: { label: 'Issue', tone: 'warning' },
  ADJUSTMENT: { label: 'Adjustment', tone: 'neutral' }
};

// Incoming order status options - frontend display labels mapped to backend enum values
const INCOMING_ORDER_STATUS_OPTIONS = [
  { value: 'On the way', label: 'In transit' },
  { value: 'Waiting space approval', label: 'Waiting space approval' },
  { value: 'Waiting manufacturer update', label: 'Waiting manufacturer update' },
  { value: 'Arrived', label: 'Arrived' },
  { value: 'Partially received', label: 'Partially received' },
  { value: 'Received', label: 'Received' },
];

// Map frontend values to backend enum values (for backward compatibility)
const STATUS_TO_BACKEND_MAP = {
  'ON_THE_WAY': 'On the way',
  'WAITING_SPACE_APPROVAL': 'Waiting space approval',
  'WAITING_MANUFACTURER_UPDATE': 'Waiting manufacturer update',
  'PARTIAL': 'Arrived',
  'RECEIVED': 'Received',
  'CANCELLED': 'Received',
  'In Transit': 'On the way',
  'In Store': 'Arrived',
  'logged into system': 'Received',
};

// Helper to normalize status to backend enum value
const normalizeStatus = (status) => {
  if (!status) return 'On the way';
  // If it's already a valid backend enum value, return as-is
  if (INCOMING_ORDER_STATUS_OPTIONS.some(opt => opt.value === status)) {
    return status;
  }
  // Map from frontend/legacy values to backend enum
  if (STATUS_TO_BACKEND_MAP[status]) {
    return STATUS_TO_BACKEND_MAP[status];
  }
  return 'On the way'; // Default fallback
};

// Helper to get display label for a status value
const getStatusLabel = (status) => {
  if (!status) return 'In transit';
  const normalized = normalizeStatus(status);
  const option = INCOMING_ORDER_STATUS_OPTIONS.find(opt => opt.value === normalized);
  return option ? option.label : normalized;
};

// Helper to get status tone/color for incoming orders
const getIncomingOrderStatusTone = (status) => {
  if (!status) return 'neutral';
  const normalized = normalizeStatus(status);
  
  switch (normalized) {
    case 'On the way':
      return 'neutral'; // Blue - in transit
    case 'Waiting space approval':
      return 'warning'; // Orange - waiting
    case 'Waiting manufacturer update':
      return 'warning'; // Orange - waiting
    case 'Arrived':
      return 'normal'; // Green - arrived
    case 'Partially received':
      return 'warning'; // Orange - partially received
    case 'Received':
      return 'normal'; // Green - completed
    default:
      return 'neutral';
  }
};

const STATUS_BADGE = {
  normal: { tone: 'normal', label: 'OK', icon: CircleCheckBig },
  warning: { tone: 'warning', label: 'Expiring Soon', icon: AlertTriangle },
  critical: { tone: 'critical', label: 'Expired', icon: CircleAlert }
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatNumber = (value) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(value);

const getDaysUntil = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const diff = date.getTime() - now.getTime();

  return Math.round(diff / (1000 * 60 * 60 * 24));
};

const resolveStatus = (expiryDate, status) => {
  if (!expiryDate) {
    return STATUS_BADGE.normal;
  }

  const daysUntilExpiry = getDaysUntil(expiryDate);

  if (status === 'Closed') {
    return { tone: 'neutral', label: 'Closed', icon: CircleCheckBig };
  }

  if (daysUntilExpiry < 0) {
    return STATUS_BADGE.critical;
  }

  if (daysUntilExpiry <= 90) {
    return STATUS_BADGE.warning;
  }

  return STATUS_BADGE.normal;
};

const normaliseHsCode = (value) => {
  if (!value) {
    return '';
  }

  return value.toString().trim().toUpperCase();
};

const InventoryManagement = () => {
  const { user } = useContext(AuthContext);
  const isSuperUser = user?.role === 'super';
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [batchesSearchTerm, setBatchesSearchTerm] = useState('');
  const [movementsSearchTerm, setMovementsSearchTerm] = useState('');
  const [incomingOrdersSearchTerm, setIncomingOrdersSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [movementFilters, setMovementFilters] = useState({
    productId: 'all',
    startDate: '',
    endDate: ''
  });
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [modal, setModal] = useState(null);
  const [metrics, setMetrics] = useState({
    activeProducts: 0,
    expiringSoon: 0,
    expired: 0,
    incomingOrdersPending: 0
  });
  const [inventoryProducts, setInventoryProducts] = useState([]);
  const [costCardProducts, setCostCardProducts] = useState([]);
  const [currentStock, setCurrentStock] = useState([]);
  const [batchList, setBatchList] = useState([]);
  const [movementList, setMovementList] = useState([]);
  const [incomingOrders, setIncomingOrders] = useState([]);
  const [loading, setLoading] = useState({
    dashboard: false,
    products: false,
    stock: false,
    batches: false,
    movements: false,
    orders: false,
    action: false
  });
  const [errors, setErrors] = useState({
    action: null,
    actionSuggestion: null
  });
  const [incomingOrderForm, setIncomingOrderForm] = useState({
    orderNo: '',
    supplier: '',
    pfiNo: '',
    productId: '',
    productName: '',
    poNo: '',
    qtyOrdered: '',
    unit: 'kg',
    etd: '',
    eta: '',
    bl: '',
    vesselName: '',
    position: '',
    status: 'On the way',
    remarks: ''
  });
  const [incomingOrderAttachments, setIncomingOrderAttachments] = useState([]);
  const [uploadingIncomingOrderFiles, setUploadingIncomingOrderFiles] = useState(false);
  const incomingOrderFileInputRef = useRef(null);
  const [incomingOrderFormSaving, setIncomingOrderFormSaving] = useState(false);
  const [incomingOrderFormError, setIncomingOrderFormError] = useState(null);
  const [editBatchModal, setEditBatchModal] = useState(null); // { batchId, productName }
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportSelections, setExportSelections] = useState({
    currentStockSummary: false,
    batchRegister: false,
    stockMovement: false,
    incomingOrders: false,
    riskExpiry: false,
    fullPack: false
  });
  const [exportFilters, setExportFilters] = useState({
    startDate: '',
    endDate: '',
    productId: 'all',
    movementType: 'all'
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [editBatchForm, setEditBatchForm] = useState({
    batchNo: '',
    productionDate: '',
    expiryDate: '',
    status: 'Open',
    countryOfOrigin: '',
    pfiOrPoNumber: '',
    attachments: []
  });
  const [editBatchSaving, setEditBatchSaving] = useState(false);
  const [editBatchError, setEditBatchError] = useState(null);

  // Helper to normalize product names for comparison (handles whitespace, case, etc.)
  const normalizeProductName = useCallback((name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
      .replace(/\s*\(\s*/g, ' (') // Normalize spaces around parentheses
      .replace(/\s*\)\s*/g, ') ')
      .trim();
  }, []);

  const clearActionError = useCallback(() => {
    setErrors((prev) => ({
      ...prev,
      action: null,
      actionSuggestion: null
    }));
  }, []);

  const authFetch = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Add timeout if not already provided
    const controller = options.signal || new AbortController();
    const timeoutId = !options.signal ? setTimeout(() => controller.abort(), 60000) : null;

    try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
        headers,
        signal: controller.signal
    });

      if (timeoutId) clearTimeout(timeoutId);

    if (!response.ok) {
      let message = 'Request failed';
      let body = null;
      try {
        body = await response.json();
        message = body.message || body.error || message;
          
          // Handle specific error cases
          if (response.status === 429) {
            message = body.message || 'Too many requests. Please wait a few minutes before trying again.';
          } else if (response.status === 500) {
            message = body.message || 'Server error. Please try again later.';
          } else if (response.status === 503) {
            message = body.message || 'Service temporarily unavailable. Please try again later.';
          }
      } catch (error) {
        console.error('Failed to parse error response', error);
          const text = await response.text().catch(() => '');
          message = text || message;
      }
      const err = new Error(message);
      err.status = response.status;
      if (body && typeof body === 'object') {
        err.details = body;
      }
      throw err;
    }

    return response.json();
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please check your connection and try again.');
      }
      throw error;
    }
  };

  const normaliseDoc = (doc) => {
    if (!doc || typeof doc !== 'object') {
      return doc;
    }
    const base = { ...doc };
    if (base._id && !base.id) {
      base.id = base._id;
    }
    if (base.product) {
      base.product = normaliseDoc(base.product);
    }
    if (base.batch) {
      base.batch = normaliseDoc(base.batch);
    }
    // Normalize user field (populated with name and email)
    if (base.user) {
      if (typeof base.user === 'object' && !base.user._id && !base.user.id) {
        // Already populated user object, keep as-is
        base.user = base.user;
      } else if (base.user._id && !base.user.id) {
        base.user.id = base.user._id;
      }
    }
    // Normalize checkedBy field (populated with name and email)
    if (base.checkedBy) {
      if (typeof base.checkedBy === 'object') {
        // If it's an object (populated user), preserve it
        if (base.checkedBy._id && !base.checkedBy.id) {
          base.checkedBy.id = base.checkedBy._id;
        }
        // Keep the populated object as-is (it has name and email)
        base.checkedBy = base.checkedBy;
      } else if (typeof base.checkedBy === 'string') {
        // If it's just an ObjectId string, we can't populate it here
        // It should be populated by the backend, but keep it for reference
        base.checkedBy = base.checkedBy;
      }
    }
    // Ensure attachments array is preserved and properly formatted
    if (base.attachments) {
      if (Array.isArray(base.attachments)) {
        // Keep the array as-is
        base.attachments = base.attachments;
      } else if (typeof base.attachments === 'object') {
        // If it's a single object, wrap it in an array
        base.attachments = [base.attachments];
      } else {
        // If it's something else, set to empty array
        base.attachments = [];
      }
    } else {
      // If attachments is null/undefined, ensure it's an empty array
      base.attachments = [];
    }
    return base;
  };

  const loadProducts = async () => {
    setLoading((prev) => ({ ...prev, products: true }));
    try {
      const payload = await authFetch('/products/options');
      console.log('[loadProducts] API response:', payload);
      const inventory = (payload.data?.inventory || []).map(normaliseDoc);
      const inventoryNames = new Set(inventory.map((item) => item.productName));
      // IMPORTANT: Always include ALL costcards, even if they have similar names to inventory products
      // CostCard products are separate entities and should be selectable
      const costCards = (payload.data?.costCards || []).map(normaliseDoc);
      console.log('[loadProducts] Loaded:', {
        inventoryCount: inventory.length,
        costCardsCount: costCards.length,
        costCards: costCards.slice(0, 3).map(c => c.productName)
      });
      setInventoryProducts(inventory);
      setCostCardProducts(costCards);
    } catch (error) {
      console.error('Failed to load products', error);
      setErrors((prev) => ({ ...prev, products: error.message }));
    } finally {
      setLoading((prev) => ({ ...prev, products: false }));
    }
  };

  const fetchCostCardDetails = async (productName) => {
    if (!productName) {
      return null;
    }

    try {
      const payload = await authFetch(
        `/costcard-products?name=${encodeURIComponent(productName)}`
      );
      return payload.data || null;
    } catch (error) {
      console.error('Failed to load cost card product detail', error);
      return null;
    }
  };

  const loadCurrentStock = async () => {
    setLoading((prev) => ({ ...prev, stock: true }));
    try {
      const payload = await authFetch('/current-stock');
      const stock = (payload.data || []).map(normaliseDoc);
      console.log('Loaded current stock:', stock.length, 'items');
      // Log sample data to verify countryOfOrigin is present
      if (stock.length > 0) {
        console.log('Sample current stock item:', {
          batchNo: stock[0].batchNo,
          countryOfOrigin: stock[0].countryOfOrigin,
          hasCountryOfOrigin: !!stock[0].countryOfOrigin,
          fullItem: stock[0]
        });
      }
      setCurrentStock(stock);
    } catch (error) {
      console.error('Failed to load current stock', error);
      setErrors((prev) => ({ ...prev, stock: error.message }));
    } finally {
      setLoading((prev) => ({ ...prev, stock: false }));
    }
  };

  const loadBatches = async () => {
    setLoading((prev) => ({ ...prev, batches: true }));
    try {
      const payload = await authFetch('/batches');
      const batches = (payload.data || []).map(normaliseDoc);
      console.log('Loaded batches:', batches.length, 'batches');
      setBatchList(batches);
    } catch (error) {
      console.error('Failed to load batches', error);
      setErrors((prev) => ({ ...prev, batches: error.message }));
    } finally {
      setLoading((prev) => ({ ...prev, batches: false }));
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!isSuperUser) {
      alert('Only super users can delete products');
      return;
    }

    // Check if product has dependencies
    const product = inventoryProducts.find(p => (p.id || p._id) === productId);
    const hasBatches = batchList.some(b => String(b.product?.id || b.product?._id || b.productId) === String(productId));
    const hasMovements = movementList.some(m => String(m.product?.id || m.product?._id || m.productId) === String(productId));

    let message = `Are you sure you want to delete the product "${productName}"?\n\n`;
    if (hasBatches || hasMovements) {
      message += `⚠️ WARNING: This product has related data:\n`;
      if (hasBatches) message += `- Has batches associated\n`;
      if (hasMovements) message += `- Has stock movements associated\n\n`;
      message += `Would you like to DELETE EVERYTHING (cascade delete)?\n\n`;
      message += `Click OK to delete everything, or Cancel to abort.`;
    } else {
      message += `This action cannot be undone.`;
    }

    const confirmed = window.confirm(message);
    if (!confirmed) {
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, action: true }));
      
      // Use cascade delete if there are dependencies
      const cascadeParam = (hasBatches || hasMovements) ? '?cascade=true' : '';
      const response = await fetch(`${API_BASE}/products/${productId}${cascadeParam}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete product');
      }

      let successMessage = 'Product deleted successfully';
      if (data.deleted) {
        successMessage += `\n\nDeleted:\n`;
        successMessage += `- Product: 1\n`;
        if (data.deleted.movements > 0) successMessage += `- Stock movements: ${data.deleted.movements}\n`;
        if (data.deleted.batches > 0) successMessage += `- Batches: ${data.deleted.batches}\n`;
        if (data.deleted.incomingOrders > 0) successMessage += `- Incoming orders: ${data.deleted.incomingOrders}\n`;
      }
      alert(successMessage);
      
      // Refresh data
      await loadProducts();
      await loadCurrentStock();
      await loadBatches();
      await loadMovements();
      await loadIncomingOrders();
      await loadDashboard(); // Refresh dashboard metrics
    } catch (error) {
      console.error('Error deleting product:', error);
      alert(`Error deleting product: ${error.message}`);
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  const handleDeleteBatch = async (batchId, batchNo, productName) => {
    if (!isSuperUser) {
      alert('Only super users can delete batches');
      return;
    }

    // Check if batch has dependencies
    const batch = batchList.find(b => (b.id || b._id) === batchId);
    const hasMovements = movementList.some(m => String(m.batch?.id || m.batch?._id || m.batchId) === String(batchId));
    const hasStock = batch && (batch.currentQtyKg || 0) > 0;

    let message = `Are you sure you want to delete batch "${batchNo}" for product "${productName}"?\n\n`;
    if (hasMovements || hasStock) {
      message += `⚠️ WARNING: This batch has related data:\n`;
      if (hasMovements) message += `- Has stock movements associated\n`;
      if (hasStock) message += `- Has ${batch.currentQtyKg} kg of stock remaining\n\n`;
      message += `Would you like to DELETE EVERYTHING (cascade delete)?\n\n`;
      message += `Click OK to delete everything, or Cancel to abort.`;
    } else {
      message += `This action cannot be undone.`;
    }

    const confirmed = window.confirm(message);
    if (!confirmed) {
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, action: true }));
      
      // Use cascade delete if there are dependencies
      const cascadeParam = (hasMovements || hasStock) ? '?cascade=true' : '';
      const response = await fetch(`${API_BASE}/batches/${batchId}${cascadeParam}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete batch');
      }

      alert(data.message || 'Batch deleted successfully');
      // Refresh data
      await loadBatches();
      await loadCurrentStock();
      await loadMovements();
      await loadDashboard(); // Refresh dashboard metrics
    } catch (error) {
      console.error('Error deleting batch:', error);
      alert(`Error deleting batch: ${error.message}`);
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  const handleDeleteStockMovement = async (movementId, movementType, movementDate) => {
    if (!isSuperUser) {
      alert('Only super users can delete stock movements');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete this ${movementType} movement from ${movementDate}?\n\n` +
      `This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, action: true }));
      const response = await fetch(`${API_BASE}/stock-movements/${movementId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete stock movement');
      }

      alert('Stock movement deleted successfully');
      // Refresh data
      await loadMovements();
      await loadBatches();
      await loadCurrentStock();
      await loadDashboard(); // Refresh dashboard metrics
    } catch (error) {
      console.error('Error deleting stock movement:', error);
      alert(`Error deleting stock movement: ${error.message}`);
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  const handleDeleteIncomingOrder = async (orderId, orderNo, productName) => {
    if (!isSuperUser) {
      alert('Only super users can delete incoming orders');
      return;
    }

    // Check if order has related stock movements
    const hasMovements = movementList.some(m => 
      m.incomingOrder && String(m.incomingOrder?.id || m.incomingOrder?._id || m.incomingOrder) === String(orderId)
    );

    let message = `Are you sure you want to delete incoming order "${orderNo}" for product "${productName}"?\n\n`;
    if (hasMovements) {
      message += `⚠️ WARNING: This order has stock movements associated with it.\n\n`;
      message += `Would you like to DELETE THE ORDER (cascade delete will remove the association)?\n\n`;
      message += `Click OK to delete the order, or Cancel to abort.`;
    } else {
      message += `This action cannot be undone.`;
    }

    const confirmed = window.confirm(message);
    if (!confirmed) {
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, action: true }));
      
      // Use cascade delete if there are related movements
      const cascadeParam = hasMovements ? '?cascade=true' : '';
      const response = await fetch(`${API_BASE}/incoming-orders/${orderId}${cascadeParam}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete incoming order');
      }

      alert(data.message || 'Incoming order deleted successfully');
      // Refresh data
      await loadIncomingOrders();
      await loadMovements();
      await loadDashboard(); // Refresh dashboard metrics
    } catch (error) {
      console.error('Error deleting incoming order:', error);
      alert(`Error deleting incoming order: ${error.message}`);
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  const loadMovements = async () => {
    setLoading((prev) => ({ ...prev, movements: true }));
    try {
      const payload = await authFetch('/stock-movements');
      const movements = (payload.data || []).map(normaliseDoc);
      console.log('Loaded movements:', movements.length, 'movements');
      setMovementList(movements);
    } catch (error) {
      console.error('Failed to load stock movements', error);
      setErrors((prev) => ({ ...prev, movements: error.message }));
    } finally {
      setLoading((prev) => ({ ...prev, movements: false }));
    }
  };

  const loadIncomingOrders = async () => {
    setLoading((prev) => ({ ...prev, orders: true }));
    try {
      const payload = await authFetch(
        orderStatusFilter === 'all'
          ? '/incoming-orders'
          : `/incoming-orders?status=${encodeURIComponent(orderStatusFilter)}`
      );
      setIncomingOrders((payload.data || []).map(normaliseDoc));
    } catch (error) {
      console.error('Failed to load incoming orders', error);
      setErrors((prev) => ({ ...prev, orders: error.message }));
    } finally {
      setLoading((prev) => ({ ...prev, orders: false }));
    }
  };

  const loadDashboard = async () => {
    setLoading((prev) => ({ ...prev, dashboard: true }));
    try {
      const payload = await authFetch('/dashboard');
      console.log('[Dashboard Frontend] Received payload:', payload);
      if (payload.data) {
        console.log('[Dashboard Frontend] Setting metrics:', payload.data);
        setMetrics(payload.data);
      }
    } catch (error) {
      console.error('Failed to load dashboard metrics', error);
      setErrors((prev) => ({ ...prev, dashboard: error.message }));
    } finally {
      setLoading((prev) => ({ ...prev, dashboard: false }));
    }
  };

  const refreshAfterMutation = async () => {
    try {
      console.log('Starting refreshAfterMutation...');
    await Promise.all([
      loadProducts(),
      loadCurrentStock(),
      loadBatches(),
      loadMovements(),
      loadIncomingOrders(),
      loadDashboard()
    ]);
      console.log('refreshAfterMutation completed successfully');
    } catch (error) {
      console.error('Error in refreshAfterMutation:', error);
      // Still refresh what we can
      try {
        await loadCurrentStock();
        await loadBatches();
        await loadMovements();
      } catch (refreshError) {
        console.error('Error refreshing individual data:', refreshError);
      }
    }
  };

  const handleExportReport = async () => {
    const { fullPack, ...rest } = exportSelections;
    const selectedTypes = fullPack
      ? ['full-pack']
      : Object.entries(rest)
          .filter(([, v]) => v)
          .map(([k]) => ({
            currentStockSummary: 'current-stock-summary',
            batchRegister: 'batch-register',
            stockMovement: 'stock-movement',
            incomingOrders: 'incoming-orders',
            riskExpiry: 'risk-expiry'
          }[k]))
          .filter(Boolean);
    if (selectedTypes.length === 0) {
      alert('Please select at least one report type or Full Inventory Pack.');
      return;
    }
    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      selectedTypes.forEach((t) => params.append('type', t));
      if (exportFilters.startDate) params.set('startDate', exportFilters.startDate);
      if (exportFilters.endDate) params.set('endDate', exportFilters.endDate);
      if (exportFilters.productId && exportFilters.productId !== 'all') params.set('productId', exportFilters.productId);
      if (exportFilters.movementType && exportFilters.movementType !== 'all') params.set('movementType', exportFilters.movementType);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/export?${params.toString()}`, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.message || `Export failed: ${response.status}`);
      }
      const blob = await response.blob();
      const dateStr = new Date().toISOString().slice(0, 10);
      const filename = `Inventory_Report_${dateStr}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setExportModalOpen(false);
    } catch (error) {
      console.error('Export failed', error);
      alert(error.message || 'Export failed. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
    loadCurrentStock();
    loadBatches();
    loadMovements();
    loadIncomingOrders();
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDateInputValue = (value) => {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toISOString().slice(0, 10);
  };

  useEffect(() => {
    if (modal?.type === 'receive-order') {
      const order = modal.context?.order || {};
      const productId =
        order.product?.id ?? order.product?._id ?? order.productId ?? '';
      const productName = order.product?.productName ?? order.productName ?? '';

      setIncomingOrderForm({
        orderNo: order.orderNo ?? '',
        supplier: order.supplier ?? '',
        pfiNo: order.pfiNo ?? '',
        productId,
        productName,
        poNo: order.poNo ?? '',
        qtyOrdered: order.qtyOrdered != null ? String(order.qtyOrdered) : '',
        unit: order.unit ?? 'kg',
        etd: formatDateInputValue(order.etd),
        eta: formatDateInputValue(order.eta),
        bl: order.bl ?? '',
        vesselName: order.vesselName ?? '',
        position: order.position ?? '',
        status: normalizeStatus(order.status),
        remarks: order.remarks ?? ''
      });
      setIncomingOrderFormError(null);
    } else if (modal?.type === 'register-order') {
      setIncomingOrderForm({
        orderNo: '',
        supplier: '',
        pfiNo: '',
        productId: '',
        productName: '',
        poNo: '',
        qtyOrdered: '',
        unit: 'kg',
        etd: '',
        eta: '',
        bl: '',
        vesselName: '',
        position: '',
        status: 'On the way',
        remarks: ''
      });
      setIncomingOrderFormError(null);
    }
  }, [modal, inventoryProducts]);

  const filteredStock = useMemo(() => {
    if (!searchTerm || !searchTerm.trim()) {
      return currentStock;
    }

    const keyword = searchTerm.trim().toLowerCase();
    return currentStock.filter((row) => {
      const productName = (row.product?.productName || row.productName || '').toString().toLowerCase();
      const productCode = (row.product?.productCode || row.productCode || '').toString().toLowerCase();
      const batchNo = (row.batchNo || '').toString().toLowerCase();
      
      return (
        productName.includes(keyword) ||
        productCode.includes(keyword) ||
        batchNo.includes(keyword)
      );
    });
  }, [currentStock, searchTerm]);

  const statusFilteredStock = useMemo(() => {
    return filteredStock.filter((row) => {
        if (statusFilter === 'all') {
          return true;
        }

        const status = resolveStatus(row.expiryDate, row.status);
        if (statusFilter === 'expiring') {
          return status.tone === 'warning';
        }

        if (statusFilter === 'expired') {
          return status.tone === 'critical';
        }

        if (statusFilter === 'zero') {
          return (row.currentQtyKg ?? 0) <= 0;
        }

        return true;
      });
  }, [filteredStock, statusFilter]);

  const sortedStock = useMemo(() => {
    return [...statusFilteredStock].sort((a, b) => {
        const statusA = resolveStatus(a.expiryDate, a.status);
        const statusB = resolveStatus(b.expiryDate, b.status);

        if (statusA.tone !== statusB.tone) {
          const priority = { critical: 0, warning: 1, normal: 2, neutral: 3 };
          return priority[statusA.tone] - priority[statusB.tone];
        }

        const nameComparison = (a.product?.productName || '').localeCompare(
          b.product?.productName || ''
        );
        if (nameComparison !== 0) {
          return nameComparison;
        }

        return new Date(a.expiryDate) - new Date(b.expiryDate);
    });
  }, [statusFilteredStock]);

  const groupedStock = useMemo(() => {
    return sortedStock.reduce((acc, row) => {
      const productId = row.product?.id ?? row.product?._id ?? row.productId ?? 'unknown';
      if (!acc[productId]) {
        acc[productId] = {
          productId: productId !== 'unknown' ? productId : null,
          productName: row.product?.productName ?? row.productName ?? productId,
          productCode: row.product?.productCode ?? row.productCode,
          rows: []
        };
      }

      acc[productId].rows.push(row);

      return acc;
    }, {});
  }, [sortedStock]);

  const issueProductSummary = useMemo(() => {
    const map = new Map();

    currentStock.forEach((row) => {
      const productId =
        row.product?.id ?? row.product?._id ?? row.productId ?? row.product ?? null;
      if (!productId) {
        return;
      }

      const existing = map.get(productId) || {
        productId,
        productName: row.product?.productName ?? row.productName ?? '—',
        hsCode: row.product?.productCode ?? row.productCode ?? '',
        totalQty: 0,
        batches: []
      };

      existing.totalQty += row.currentQtyKg ?? 0;
      existing.batches.push(row);
      map.set(productId, existing);
    });

    return Array.from(map.values()).sort((a, b) =>
      a.productName.localeCompare(b.productName)
    );
  }, [currentStock]);

  const filteredBatches = useMemo(() => {
    if (!batchesSearchTerm || !batchesSearchTerm.trim()) {
      return batchList;
    }

    const keyword = batchesSearchTerm.trim().toLowerCase();
    return batchList.filter((batch) => {
      const productName = (batch.product?.productName || batch.productName || '').toString().toLowerCase();
      const productCode = (batch.product?.productCode || batch.productCode || '').toString().toLowerCase();
      const batchNo = (batch.batchNo || '').toString().toLowerCase();
      const countryOfOrigin = (batch.countryOfOrigin || '').toString().toLowerCase();
      
      return (
        productName.includes(keyword) ||
        productCode.includes(keyword) ||
        batchNo.includes(keyword) ||
        countryOfOrigin.includes(keyword)
      );
    });
  }, [batchList, batchesSearchTerm]);

  const batchesWithProduct = useMemo(() => {
    return filteredBatches.map((batch) => {
      const resolvedCreatedBy =
        batch.createdByName ??
        batch.createdBy?.name ??
        batch.createdBy?.email ??
        (typeof batch.createdBy === 'string' ? batch.createdBy : '');
      const resolvedUpdatedBy =
        batch.updatedByName ??
        batch.updatedBy?.name ??
        batch.updatedBy?.email ??
        (typeof batch.updatedBy === 'string' ? batch.updatedBy : resolvedCreatedBy);

      return {
        ...batch,
        productName: batch.product?.productName ?? batch.productId ?? batch.product ?? '—',
        createdByName: resolvedCreatedBy,
        updatedByName: resolvedUpdatedBy
      };
    });
  }, [filteredBatches]);

  const filteredMovements = useMemo(() => {
    return movementList.filter((movement) => {
      const { productId, startDate, endDate } = movementFilters;

      if (productId !== 'all') {
        const movementProductId =
          movement.product?.id ?? movement.product?._id ?? movement.productId;
        if (movementProductId !== productId) {
          return false;
        }
      }

      if (startDate) {
        const start = new Date(startDate);
        const movementDate = new Date(movement.date);
        if (movementDate < start) {
          return false;
        }
      }

      if (endDate) {
        const end = new Date(endDate);
        const movementDate = new Date(movement.date);
        if (movementDate > end) {
          return false;
        }
      }

      return true;
    });
  }, [movementFilters, movementList]);

  const filteredOrders = useMemo(() => {
    let orders = incomingOrders;

    // Filter by status
    if (orderStatusFilter !== 'all') {
      orders = orders.filter((order) => {
        const normalizedOrderStatus = normalizeStatus(order.status);
        return normalizedOrderStatus === orderStatusFilter;
      });
    }

    // Filter by search term
    if (incomingOrdersSearchTerm && incomingOrdersSearchTerm.trim()) {
      const keyword = incomingOrdersSearchTerm.trim().toLowerCase();
      orders = orders.filter((order) => {
        const supplier = (order.supplier || '').toString().toLowerCase();
        const pfiNo = (order.pfiNo || '').toString().toLowerCase();
        const productName = (order.product?.productName || order.productName || '').toString().toLowerCase();
        const poNo = (order.poNo || '').toString().toLowerCase();
        const bl = (order.bl || '').toString().toLowerCase();
        const vesselName = (order.vesselName || '').toString().toLowerCase();
        const position = (order.position || '').toString().toLowerCase();
        const orderNo = (order.orderNo || '').toString().toLowerCase();
        
        return (
          supplier.includes(keyword) ||
          pfiNo.includes(keyword) ||
          productName.includes(keyword) ||
          poNo.includes(keyword) ||
          bl.includes(keyword) ||
          vesselName.includes(keyword) ||
          position.includes(keyword) ||
          orderNo.includes(keyword)
        );
      });
    }

    return orders;
  }, [incomingOrders, orderStatusFilter, incomingOrdersSearchTerm]);

  const expiringSoonCount = useMemo(() => {
    return currentStock.filter(
      (row) => resolveStatus(row.expiryDate, row.status).tone === 'warning'
    ).length;
  }, [currentStock]);

  const expiredCount = useMemo(() => {
    return currentStock.filter(
      (row) => resolveStatus(row.expiryDate, row.status).tone === 'critical'
    ).length;
  }, [currentStock]);

  const zeroBalanceCount = useMemo(() => {
    return currentStock.filter((row) => (row.currentQtyKg ?? 0) <= 0).length;
  }, [currentStock]);

  const incomingPendingCount = useMemo(() => {
    if (metrics?.incomingOrdersPending != null) {
      return metrics.incomingOrdersPending;
    }
    if (metrics?.incomingAwaitingSpaceApproval != null) {
      return metrics.incomingAwaitingSpaceApproval;
    }
    return incomingOrders.filter((order) => {
      const status = normalizeStatus(order.status);
      return status !== 'Received';
    }).length;
  }, [incomingOrders, metrics]);

  const recommendedBatchForProduct = (productId) => {
    return currentStock
      .filter((batch) => {
        const id =
          batch.product?.id ?? batch.product?._id ?? batch.productId ?? batch.product ?? '';
        return (
          id === productId &&
          resolveStatus(batch.expiryDate, batch.status).tone !== 'critical' &&
          (batch.currentQtyKg ?? 0) > 0
        );
      })
      .sort((a, b) => new Date(a.expiryDate) - new Date(b.expiryDate))[0];
  };

  const handleMovementFilterChange = (field, value) => {
    setMovementFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleReceiveSubmit = async (data) => {
    setLoading((prev) => ({ ...prev, action: true }));
    clearActionError();
    try {
      let productId = data.productId;

      // Handle CostCard products - they don't have productId, only productName
      if (!productId && data.productSource === 'costcard' && data.productName) {
        // For CostCard products, check if an inventory product with EXACT name exists
        // Use exact match first, then case-insensitive match
        let existingProduct = inventoryProducts.find(
          (p) => p.productName === data.productName
        );
        
        if (!existingProduct) {
          existingProduct = inventoryProducts.find(
            (p) => p.productName?.trim().toLowerCase() === data.productName?.trim().toLowerCase()
          );
        }

        if (existingProduct) {
          // Use the existing product's ID
          productId = String(existingProduct.id || existingProduct._id);
          console.log('✅ Found existing inventory product for CostCard:', {
            productId,
            productName: existingProduct.productName,
            costCardName: data.productName,
            matchType: existingProduct.productName === data.productName ? 'exact' : 'case-insensitive'
          });
        } else {
          // Create new product with EXACT CostCard name
          if (!data.hsCode) {
            throw new Error('HS code is required when creating a new product from CostCard');
          }

          const standardPackValue =
            data.productStandardPack === '' || data.productStandardPack == null
              ? null
              : Number(data.productStandardPack);

          console.log('📝 Creating new inventory product from CostCard:', {
            productName: data.productName,
            productCode: data.hsCode,
            unit: data.productUnit || 'kg'
          });

          const createdResponse = await authFetch('/products', {
            method: 'POST',
            body: JSON.stringify({
              productCode: data.hsCode,
              productName: data.productName, // Use EXACT name from CostCard
              unit: data.productUnit || 'kg',
              standardPack: standardPackValue
            })
          });

          const createdProduct = normaliseDoc(createdResponse.data);
          productId = createdProduct?.id ?? createdProduct?._id;

      if (!productId) {
            throw new Error('Failed to create inventory product from CostCard');
          }

          console.log('✅ Created new inventory product:', {
            productId,
            productName: createdProduct.productName || data.productName
          });

          // Reload products to include the newly created one
          await loadProducts();
        }
      } else if (productId) {
        // Validate productId if it's provided - ensure it's a valid MongoDB ObjectId format
        // Verify the product exists in our inventory list to ensure exact mapping
        const selectedProduct = inventoryProducts.find(
          (p) => String(p.id || p._id) === String(productId)
        );
        
        if (!selectedProduct) {
          console.error('❌ Selected product not found in inventory:', {
            productId,
            productName: data.productName,
            availableProducts: inventoryProducts.map(p => ({
              id: p.id || p._id,
              name: p.productName
            })).slice(0, 10)
          });
          throw new Error(`Selected product (${data.productName || productId}) not found in inventory. Please select the product again.`);
        }

        // Use the exact product ID from the found product to ensure correct mapping
        productId = String(selectedProduct.id || selectedProduct._id);
        
        console.log('✅ Validated product for receive stock:', {
          productId,
          productName: selectedProduct.productName,
          productCode: selectedProduct.productCode,
          requestedName: data.productName
        });
      } else if (!productId) {
        // Handle new product creation (not from CostCard)
        if (!data.productName || !data.hsCode) {
          throw new Error('Product name and HS code are required for new products');
        }

        const standardPackValue =
          data.productStandardPack === '' || data.productStandardPack == null
            ? null
            : Number(data.productStandardPack);

        const createdResponse = await authFetch('/products', {
          method: 'POST',
          body: JSON.stringify({
            productCode: data.hsCode,
            productName: data.productName,
            unit: data.productUnit || 'kg',
            standardPack: standardPackValue
          })
        });

        const createdProduct = normaliseDoc(createdResponse.data);
        productId = createdProduct?.id ?? createdProduct?._id;

        if (!productId) {
          throw new Error('Failed to create inventory product');
        }
      }

      // Log the exact product being sent to backend
      console.log('📤 Sending receive stock request:', {
        productId,
        productName: data.productName,
        batchNo: data.batchNo,
        quantityKg: Number(data.qty),
        countryOfOrigin: data.countryOfOrigin,
        formStateCountryOfOrigin: data.countryOfOrigin
      });

      // Make the receive stock request with proper error handling
      const response = await authFetch('/receive', {
        method: 'POST',
        body: JSON.stringify({
          productId,
          batchNo: data.batchNo,
          productionDate: data.productionDate,
          expiryDate: data.expiryDate,
          quantityKg: Number(data.qty),
          reference: data.reference,
          pfiOrPoNumber: data.pfiOrPoNumber || data.reference || '',
          remarks: data.remarks,
          countryOfOrigin: (data.countryOfOrigin && data.countryOfOrigin.trim()) || '',
          attachments: data.attachments || [],
          checkedBy: data.checkedBy || null,
          orderId: data.orderId || null // Include orderId when receiving from incoming order
        })
      });
      
      console.log('Receive stock response:', response);
      
      if (!response || !response.success) {
        throw new Error(response?.message || 'Failed to receive stock');
      }
      
      console.log('Stock received successfully, batch ID:', response.data?.batch?._id || response.data?.batch?.id);
      console.log('Movement ID:', response.data?.movement?._id || response.data?.movement?.id);
      
      // Refresh all data after successful receipt - do this sequentially to ensure proper loading
      console.log('Refreshing data after receive stock...');
      try {
        // First refresh using the standard function
      await refreshAfterMutation();
        console.log('Initial refresh completed');
        
        // Wait a bit for state to update
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Force explicit reloads to ensure data is fresh
        console.log('Performing explicit data reloads...');
        await loadBatches();
        console.log('Batches reloaded');
        
        await loadMovements();
        console.log('Movements reloaded');
        
        await loadCurrentStock();
        console.log('Current stock reloaded');
        
        // Switch to current stock tab to show the newly received stock
        setActiveTab('current-stock');
        
        console.log('All data refresh operations completed');
      } catch (refreshError) {
        console.error('Error during refresh after receive stock:', refreshError);
        // Still try to load critical data individually
        try {
          console.log('Attempting individual data loads...');
          await loadCurrentStock();
          await loadBatches();
          await loadMovements();
          setActiveTab('current-stock');
          console.log('Individual data loads completed');
        } catch (stockError) {
          console.error('Failed to load data after receive:', stockError);
          // Last resort - at least try to reload the page data
          alert('Stock received successfully, but there was an issue refreshing the display. Please refresh the page to see the new data.');
        }
      }
      
      return true;
    } catch (error) {
      console.error('Receive stock failed', error);
      setErrors((prev) => ({
        ...prev,
        action: error.message,
        actionSuggestion: error.details?.suggestion ?? null
      }));
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  const handleIssueSubmit = async (data) => {
    setLoading((prev) => ({ ...prev, action: true }));
    clearActionError();
    try {
      await authFetch('/issue', {
        method: 'POST',
        body: JSON.stringify({
          productId: data.productId,
          batchId: data.batchId,
          quantityKg: Number(data.qty),
          reference: data.issueTo,
          poNumber: data.poNumber || '',
          remarks: data.remarks,
          checkedBy: data.checkedBy || null,
          attachments: data.attachments || [],
          customer: data.customer || ''
        })
      });
      await refreshAfterMutation();
      return true;
    } catch (error) {
      console.error('Issue stock failed', error);
      setErrors((prev) => ({
        ...prev,
        action: error.message,
        actionSuggestion: null
      }));
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  const handleAdjustSubmit = async (data) => {
    setLoading((prev) => ({ ...prev, action: true }));
    clearActionError();
    try {
      await authFetch('/adjust', {
        method: 'POST',
        body: JSON.stringify({
          productId: data.productId,
          batchId: data.batchId,
          newQuantityKg: Number(data.newQuantity),
          reason: data.reason,
          remarks: data.remarks,
          attachments: data.attachments || [],
          checkedBy: data.checkedBy || null
        })
      });
      await refreshAfterMutation();
      return true;
    } catch (error) {
      console.error('Adjust stock failed', error);
      setErrors((prev) => ({
        ...prev,
        action: error.message,
        actionSuggestion: null
      }));
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  const handleIncomingOrderSave = async () => {
    // Prevent updating orders that are already received
    if (modal?.type === 'receive-order') {
      const currentOrderStatus = normalizeStatus(modal.context?.order?.status);
      if (currentOrderStatus === 'Received') {
        setIncomingOrderFormError('Cannot update an order that has already been received.');
        return;
      }
    }

    const qtyOrdered = incomingOrderForm.qtyOrdered === '' ? '' : Number(incomingOrderForm.qtyOrdered);
    const etd = incomingOrderForm.etd;
    const status = normalizeStatus(incomingOrderForm.status || 'On the way');

    // Validate required fields
    if (!incomingOrderForm.productId) {
      setIncomingOrderFormError('Please select a product.');
      return;
    }
    if (qtyOrdered === '') {
      setIncomingOrderFormError('Quantity is required.');
      return;
    }
    if (!etd) {
      setIncomingOrderFormError('ETD (Estimated Time of Departure) is required.');
      return;
    }

    if (!Number.isFinite(qtyOrdered) || qtyOrdered < 0) {
      setIncomingOrderFormError('Quantity must be zero or greater.');
      return;
    }

    // Validate status
    const validStatuses = INCOMING_ORDER_STATUS_OPTIONS.map(opt => opt.value);
    if (!validStatuses.includes(status)) {
      setIncomingOrderFormError(`Invalid status: ${status}. Please select a valid status.`);
      return;
    }

    let resolvedProductId = incomingOrderForm.productId;

    if (!resolvedProductId || resolvedProductId === 'new:new') {
      if (!incomingOrderForm.productName) {
        setIncomingOrderFormError('Enter a product name to create a new product.');
        return;
      }

      try {
        const productResponse = await authFetch('/products', {
          method: 'POST',
          body: JSON.stringify({
            productCode: normaliseHsCode(incomingOrderForm.productName),
            productName: incomingOrderForm.productName,
            unit: incomingOrderForm.unit,
            standardPack: null
          })
        });
        resolvedProductId = productResponse.data?.id ?? productResponse.data?._id;
        if (!resolvedProductId) {
          throw new Error('Failed to create new product');
        }
        await loadProducts();
      } catch (error) {
        console.error('Create product from order failed', error);
        setIncomingOrderFormError(error.message);
        setIncomingOrderFormSaving(false);
        return;
      }
    }

    // Extract productId - for incoming orders, inventory products use direct ObjectId
    // Costcard products use format "costcard:productName"
    let finalProductId = resolvedProductId;
    
    // Check if it's a costcard product (starts with 'costcard:')
    if (resolvedProductId && resolvedProductId.startsWith('costcard:')) {
      // CRITICAL: First, find the CostCard product from the costCardProducts array using the productId
      // This ensures we get the EXACT name as stored in the CostCard, not from form state or extraction
      const extractedName = decodeURIComponent(resolvedProductId.replace('costcard:', ''));
      
      // Find the CostCard product by matching the ID or name
      let costCardProduct = costCardProducts.find(cp => {
        const cpId = `costcard:${encodeURIComponent(cp.productName)}`;
        return cpId === resolvedProductId || cp.productName === extractedName;
      });
      
      // If not found by ID, try by name (case-insensitive)
      if (!costCardProduct) {
        costCardProduct = costCardProducts.find(cp => {
          return cp.productName?.trim().toLowerCase() === extractedName?.trim().toLowerCase();
        });
      }
      
      // Use the EXACT name from the CostCard product object, not from form state or extraction
      const productName = costCardProduct?.productName?.trim() || incomingOrderForm.productName?.trim() || extractedName?.trim();
      
      if (!costCardProduct) {
        console.error('❌ CostCard product not found for:', {
          productId: resolvedProductId,
          extractedName: extractedName,
          formProductName: incomingOrderForm.productName,
          availableCostCardNames: costCardProducts.map(cp => cp.productName).slice(0, 10)
        });
        setIncomingOrderFormError(`CostCard product "${extractedName}" not found. Please select a valid product.`);
        setIncomingOrderFormSaving(false);
        return;
      }
      
      console.log('🔍 Processing CostCard product for incoming order:', {
        costCardProductName: costCardProduct.productName,
        productId: resolvedProductId,
        extractedName: extractedName,
        formProductName: incomingOrderForm.productName,
        finalProductName: productName
      });
      
      // CRITICAL: For CostCard products, we should ONLY use existing inventory products
      // if the names match EXACTLY (case-sensitive). Otherwise, always create a new product.
      // This prevents "Zinc Sulphate" from matching "Profix" or any other product.
      
      console.log('🔍 Searching for existing inventory product:', {
        costCardProductName: productName,
        inventoryProductsCount: inventoryProducts.length,
        sampleInventoryNames: inventoryProducts.map(ip => ip.productName).slice(0, 5)
      });
      
      // ONLY match if names are EXACTLY the same (case-sensitive, character-by-character)
      let inventoryProduct = inventoryProducts.find(ip => {
        const ipName = ip.productName?.trim();
        const ccName = productName?.trim();
        const isExactMatch = ipName === ccName;
        
        if (isExactMatch) {
          console.log('✅ Found EXACT match:', {
            inventoryProductName: ipName,
            costCardName: ccName,
            productId: ip.id
          });
        }
        
        return isExactMatch;
      });
      
      // DO NOT use case-insensitive matching for CostCard products
      // This prevents incorrect matches like "Zinc Sulphate" matching "Profix"
      // If the names don't match exactly, we'll create a new product
      
      if (inventoryProduct) {
        finalProductId = String(inventoryProduct.id);
        console.log('✅ Using existing inventory product (EXACT match only):', {
          productId: finalProductId,
          inventoryProductName: inventoryProduct.productName,
          costCardName: productName,
          matchVerified: inventoryProduct.productName.trim() === productName.trim()
        });
      } else {
        console.log('ℹ️ No exact match found - will create new product:', {
          costCardName: productName,
          reason: 'No inventory product with exact same name (case-sensitive)'
        });
      }
      
      if (!inventoryProduct) {
        // Product exists in costcard but not in inventory - create it with EXACT CostCard name
        try {
          // Use the EXACT name from the CostCard product object
          const exactProductName = costCardProduct.productName?.trim();
          
          if (!exactProductName) {
            throw new Error(`CostCard product has no name`);
          }
          
          console.log('📝 Creating new inventory product from CostCard:', {
            exactProductName: exactProductName,
            costCardProductName: costCardProduct.productName,
            productCode: costCardProduct?.hsCode || normaliseHsCode(exactProductName),
            unit: incomingOrderForm.unit || 'kg'
          });

          // Create the product in inventory with EXACT CostCard name
          console.log('📤 Sending product creation request:', {
            productName: exactProductName,
            productCode: costCardProduct?.hsCode || normaliseHsCode(exactProductName),
            unit: incomingOrderForm.unit || 'kg'
          });
          
          const productResponse = await authFetch('/products', {
            method: 'POST',
            body: JSON.stringify({
              productCode: costCardProduct?.hsCode || normaliseHsCode(exactProductName),
              productName: exactProductName, // Use EXACT name from CostCard product
              unit: incomingOrderForm.unit || 'kg',
              standardPack: null
            })
          });
          
          const createdProductId = productResponse.data?.id ?? productResponse.data?._id;
          const createdProductName = productResponse.data?.productName || exactProductName;
          
          if (!createdProductId) {
            throw new Error('Failed to create product from cost card - no ID returned');
          }
          
          // Verify the created product has the correct name (case-sensitive check)
          if (createdProductName.trim() !== exactProductName.trim()) {
            console.error('❌ CRITICAL: Product name mismatch after creation!', {
              expected: exactProductName,
              actual: createdProductName,
              expectedLength: exactProductName.trim().length,
              actualLength: createdProductName.trim().length
            });
            throw new Error(`Product was created with wrong name: expected "${exactProductName}" but got "${createdProductName}"`);
          }
          
          console.log('✅ Created new inventory product from CostCard:', {
            productId: createdProductId,
            productName: createdProductName,
            expectedName: exactProductName,
            nameMatches: createdProductName.trim() === exactProductName.trim(),
            verification: 'PASSED'
          });
          
          // Reload products to include the newly created one
          await loadProducts();
          
          // Use the newly created product ID
          finalProductId = String(createdProductId);
          
          console.log('✅ Final product ID for incoming order:', {
            finalProductId: finalProductId,
            productName: createdProductName
          });
        } catch (error) {
          console.error('❌ Create product from costcard failed:', error);
          setIncomingOrderFormError(`Failed to create product "${productName}" in inventory: ${error.message}`);
          setIncomingOrderFormSaving(false);
          return;
        }
      }
    }
    // If it's 'new:new', it's already handled above (creates new product)
    // Otherwise, it should already be a valid ObjectId string from inventory products

    // Validate productId exists
    if (!finalProductId || finalProductId === 'new:new') {
      setIncomingOrderFormError('Please select a valid product.');
      setIncomingOrderFormSaving(false);
      return;
    }
    
    // Validate productId looks like a MongoDB ObjectId (24 hex characters)
    // This ensures we're sending a valid ObjectId to the backend
    if (!/^[0-9a-fA-F]{24}$/.test(finalProductId)) {
      console.error('Invalid productId format:', finalProductId);
      setIncomingOrderFormError('Invalid product ID format. Please select a product from the list.');
      setIncomingOrderFormSaving(false);
      return;
    }

    // Build payload with backend field names
    // Note: orderNo is auto-generated from PFI NO. or PO NO. if not provided
    const body = {
      orderNo: incomingOrderForm.orderNo || '', // Optional - backend will auto-generate from PFI NO. or PO NO.
      supplier: incomingOrderForm.supplier || '',
      pfiNo: incomingOrderForm.pfiNo || '',
      productId: finalProductId, // Backend expects MongoDB ObjectId
      poNo: incomingOrderForm.poNo || '',
      qtyOrdered: qtyOrdered, // Backend expects qtyOrdered (not quantityOrderedKg)
      unit: incomingOrderForm.unit || 'kg',
      etd: etd,
      eta: incomingOrderForm.eta || null,
      bl: incomingOrderForm.bl || '',
      vesselName: incomingOrderForm.vesselName || '',
      position: incomingOrderForm.position || '',
      status: status, // Backend enum value (e.g., 'On the way')
      remarks: incomingOrderForm.remarks || '',
      attachments: incomingOrderAttachments
    };

    try {
      setIncomingOrderFormSaving(true);
      setIncomingOrderFormError(null);

      if (modal?.type === 'register-order') {
        await authFetch('/incoming-orders', {
          method: 'POST',
          body: JSON.stringify(body)
        });
      } else if (modal?.type === 'receive-order' && modal.context?.orderId) {
        await authFetch(`/incoming-orders/${modal.context.orderId}`, {
          method: 'PATCH',
          body: JSON.stringify(body)
        });
      }

      await loadIncomingOrders();
      setModal(null);
    } catch (error) {
      console.error('Save incoming order failed', error);
      // Provide more detailed error messages
      let errorMessage = 'Failed to create incoming order';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.details) {
        errorMessage = `Invalid data: ${error.response.data.details}`;
      }
      setIncomingOrderFormError(errorMessage);
    } finally {
      setIncomingOrderFormSaving(false);
    }
  };

  const openReceiveStockForOrder = (order) => {
    if (!order) {
      return;
    }

    // Prevent receiving stock for orders that are already fully received
    const normalizedStatus = normalizeStatus(order.status);
    if (normalizedStatus === 'Received') {
      alert('Cannot receive stock for an order that has already been fully received.');
      return;
    }

    setModal(null);
    setTimeout(() => {
      const productId = order.product?.id ?? order.product?._id ?? order.productId ?? '';
      const linkedOrderId = order.id ?? order._id;

      setModal({
        type: 'receive-stock',
        context: {
          sourceContext: 'incoming-order',
          linkedOrderId,
          orderId: linkedOrderId, // Keep for backward compatibility
          productId,
          productName: order.product?.productName ?? order.productName ?? '',
          qty: order.qtyOrdered,
          reference: order.orderNo,
          pfiOrPoNumber: order.orderNo,
          etd: order.etd,
          order: order // Keep order object for display
        }
      });
    }, 0);
  };

  const handleModalSubmit = async (type, payload) => {
    if (type === 'receive-stock') {
      return handleReceiveSubmit(payload);
    }
    if (type === 'issue') {
      return handleIssueSubmit(payload);
    }
    if (type === 'adjust') {
      return handleAdjustSubmit(payload);
    }
    return false;
  };

  const renderStatusPill = (row) => {
    const status = resolveStatus(row.expiryDate, row.status);
    const StatusIcon = status.icon ?? CircleCheckBig;

  return (
      <span className={`status-pill ${status.tone}`}>
        <StatusIcon size={16} aria-hidden="true" />
        {status.label}
      </span>
    );
  };

  const renderDashboard = () => (
    <div className="inventory-dashboard">
      <h2>Inventory Health Snapshot</h2>

      <div className="stats-grid">
        <DashboardCard
          icon={Package}
          tone="blue"
          title="Active Products"
          value={metrics.activeProducts}
          subtitle="Unique SKUs in catalogue"
        />
        <DashboardCard
          icon={CalendarClock}
          tone="yellow"
          title="Expiring in 90 days"
          value={expiringSoonCount}
          subtitle="Batches to monitor closely"
        />
        <DashboardCard
          icon={CircleAlert}
          tone="red"
          title="Expired Batches"
          value={expiredCount}
          subtitle="Require immediate attention"
        />
        <DashboardCard
          icon={ClipboardList}
          tone="purple"
          title="Incoming Orders"
          value={incomingPendingCount}
          subtitle="Awaiting receipt"
        />
      </div>

      <section className="content-section">
        <header className="section-header">
          <h3>Quick Actions</h3>
          <p>Capture receipts, issues, and adjustments directly from here.</p>
        </header>

        <div className="action-buttons">
          <button
            type="button"
            className="action-btn"
            onClick={() => setModal({ 
              type: 'receive-stock',
              context: { sourceContext: 'manual', linkedOrderId: null }
            })}
          >
            <PlusCircle size={18} aria-hidden="true" />
            Receive stock
          </button>
          <button
            type="button"
            className="action-btn secondary"
            onClick={() => setModal({ type: 'issue' })}
          >
            <MinusCircle size={18} aria-hidden="true" />
            Issue stock
          </button>
          <button
            type="button"
            className="action-btn secondary"
            onClick={() => setModal({ type: 'adjust' })}
          >
            <AlertTriangle size={18} aria-hidden="true" />
            Adjust balance
          </button>
        </div>
      </section>

      <section className="content-section">
        <header className="section-header">
          <h3>What needs attention</h3>
          <p>Keep an eye on expiring items and zero balances to avoid disruption.</p>
        </header>

        <div className="attention-grid">
          <AttentionBlock
            title="Expiring soon"
            badge={`${expiringSoonCount} batch${expiringSoonCount === 1 ? '' : 'es'}`}
            description="Review FEFO allocations and push upcoming batches into production."
            actionLabel="Review stock"
            onClick={() => setActiveTab('current-stock')}
          />
          <AttentionBlock
            title="Zero balance"
            badge={`${zeroBalanceCount} batch${zeroBalanceCount === 1 ? '' : 'es'}`}
            description="Log adjustments or create replenishment orders where needed."
            actionLabel="Check batches"
            onClick={() => setActiveTab('batches')}
          />
        </div>
      </section>
    </div>
  );

  const renderEditBatchModal = () => {
    if (!editBatchModal) return null;
    return (
      <div className="modal-overlay movement-detail-overlay" role="dialog" aria-modal="true">
        <div className="movement-detail-panel">
          <header className="movement-detail-header">
            <div>
              <h3>Edit Batch</h3>
              <p className="movement-meta">
                {editBatchModal.productName ? `Product: ${editBatchModal.productName}` : 'Edit batch metadata'}
              </p>
            </div>
            <button type="button" className="modal-close" onClick={() => setEditBatchModal(null)} aria-label="Close">
              ×
            </button>
          </header>

          <div className="movement-detail-body">
            {editBatchError && (
              <div className="alert error" style={{ marginBottom: '12px' }}>
                {editBatchError}
              </div>
            )}
            <div className="modal-form-grid">
              <div className="form-field">
                <label htmlFor="edit-batch-batchNo">Batch no</label>
                <input
                  id="edit-batch-batchNo"
                  type="text"
                  value={editBatchForm.batchNo}
                  onChange={(e) => setEditBatchForm((prev) => ({ ...prev, batchNo: e.target.value }))}
                  required
                />
              </div>

              <div className="form-field">
                <label htmlFor="edit-batch-productionDate">Production date</label>
                <input
                  id="edit-batch-productionDate"
                  type="date"
                  value={editBatchForm.productionDate}
                  onChange={(e) => setEditBatchForm((prev) => ({ ...prev, productionDate: e.target.value }))}
                  required
                />
              </div>

              <div className="form-field">
                <label htmlFor="edit-batch-expiryDate">Expiry date</label>
                <input
                  id="edit-batch-expiryDate"
                  type="date"
                  value={editBatchForm.expiryDate}
                  onChange={(e) => setEditBatchForm((prev) => ({ ...prev, expiryDate: e.target.value }))}
                  required
                />
              </div>

              <div className="form-field">
                <label htmlFor="edit-batch-status">Status</label>
                <select
                  id="edit-batch-status"
                  value={editBatchForm.status}
                  onChange={(e) => setEditBatchForm((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <option value="Open">Open</option>
                  <option value="Closed">Closed</option>
                  <option value="Expired">Expired</option>
                </select>
              </div>

              <div className="form-field">
                <label htmlFor="edit-batch-countryOfOrigin">Country of Origin</label>
                <input
                  id="edit-batch-countryOfOrigin"
                  type="text"
                  value={editBatchForm.countryOfOrigin}
                  onChange={(e) => setEditBatchForm((prev) => ({ ...prev, countryOfOrigin: e.target.value }))}
                  placeholder="e.g. India"
                />
              </div>

              <div className="form-field">
                <label htmlFor="edit-batch-pfi">PFI / PO Number</label>
                <input
                  id="edit-batch-pfi"
                  type="text"
                  value={editBatchForm.pfiOrPoNumber}
                  onChange={(e) => setEditBatchForm((prev) => ({ ...prev, pfiOrPoNumber: e.target.value }))}
                  placeholder="e.g. QUO16034"
                />
              </div>

              <div className="form-field" style={{ gridColumn: '1 / span 2' }}>
                <label>Attachments (Optional)</label>
                <small style={{ display: 'block', color: '#64748b', marginBottom: '8px' }}>
                  Upload related documents (PDF, JPEG, PNG, DOCX)
                </small>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.docx"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length === 0) return;
                      try {
                        const uploaded = await uploadFilesWithAutoChunking({ files, context: 'documents' });
                        setEditBatchForm((prev) => ({
                          ...prev,
                          attachments: [...(prev.attachments || []), ...uploaded]
                        }));
                      } catch (err) {
                        console.error('Attachment upload failed', err);
                        setEditBatchError(err.message || 'Failed to upload files');
                      } finally {
                        e.target.value = '';
                      }
                    }}
                  />
                  {(editBatchForm.attachments || []).length > 0 && (
                    <div className="file-pill-container">
                      {editBatchForm.attachments.map((file, idx) => (
                        <div key={idx} className="file-pill">
                          <span style={{ flex: 1, fontSize: '0.875rem' }}>{file.originalName || file.filename}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setEditBatchForm((prev) => ({
                                ...prev,
                                attachments: prev.attachments.filter((_, i) => i !== idx)
                              }))
                            }
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <footer className="movement-detail-footer">
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => setEditBatchModal(null)}
                disabled={editBatchSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={async () => {
                  try {
                    setEditBatchSaving(true);
                    setEditBatchError(null);
                    if (!editBatchForm.batchNo || !editBatchForm.productionDate || !editBatchForm.expiryDate) {
                      setEditBatchError('Batch number, production date, and expiry date are required.');
                      setEditBatchSaving(false);
                      return;
                    }
                    // Always include countryOfOrigin, even if it's an empty string
                    // This ensures the backend knows we want to update it
                    const body = {
                      batchNo: editBatchForm.batchNo,
                      productionDate: editBatchForm.productionDate,
                      expiryDate: editBatchForm.expiryDate,
                      status: editBatchForm.status,
                      countryOfOrigin: editBatchForm.countryOfOrigin !== undefined ? editBatchForm.countryOfOrigin : null,
                      pfiOrPoNumber: editBatchForm.pfiOrPoNumber || null,
                      attachments: editBatchForm.attachments || []
                    };
                    console.log('💾 [Edit Batch] Saving batch metadata:', { 
                      batchId: editBatchModal.batchId, 
                      body,
                      countryOfOrigin: body.countryOfOrigin,
                      countryOfOriginType: typeof body.countryOfOrigin,
                      formState: editBatchForm,
                      formCountryOfOrigin: editBatchForm.countryOfOrigin
                    });
                    const response = await authFetch(`/batches/${editBatchModal.batchId}`, {
                      method: 'PATCH',
                      body: JSON.stringify(body)
                    });
                    console.log('✅ [Edit Batch] Batch metadata save response:', response);
                    if (!response || !response.success) {
                      throw new Error(response?.message || 'Failed to update batch');
                    }
                    await loadCurrentStock();
                    await loadBatches(); // Also reload batches list
                    setEditBatchModal(null);
                  } catch (error) {
                    console.error('Save batch metadata failed', error);
                    setEditBatchError(error.message || 'Failed to update batch');
                  } finally {
                    setEditBatchSaving(false);
                  }
                }}
                disabled={editBatchSaving}
              >
                {editBatchSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </footer>
        </div>
      </div>
    );
  };

  const renderCurrentStock = () => (
    <div className="tab-content">
      <header className="tab-header">
        <div>
          <h2>Current Stock</h2>
          <p>Digitised view of the whiteboard — grouped by product and batch.</p>
        </div>
        <div className="tab-actions">
          <button
            type="button"
            className="action-btn"
            onClick={() => setModal({ 
              type: 'receive-stock',
              context: { sourceContext: 'manual', linkedOrderId: null }
            })}
          >
            <PlusCircle size={18} aria-hidden="true" />
            Receive stock
          </button>
          <button
            type="button"
            className="action-btn secondary"
            onClick={() => setModal({ type: 'issue' })}
          >
            <MinusCircle size={18} aria-hidden="true" />
            Issue stock
          </button>
          <button
            type="button"
            className="action-btn secondary"
            onClick={() => setModal({ type: 'adjust' })}
          >
            <AlertTriangle size={18} aria-hidden="true" />
            Adjust
          </button>
        </div>
      </header>

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="stock-search">Search</label>
          <input
            id="stock-search"
            type="text"
            placeholder="Search by product, code, or batch"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="stock-status">Status</label>
          <select
            id="stock-status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="expiring">Expiring soon</option>
            <option value="expired">Expired</option>
            <option value="zero">Zero balance</option>
          </select>
        </div>
      </div>

      {Object.values(groupedStock).map((group) => (
        <section key={group.productCode ?? group.productName} className="stock-group">
          <header className="group-header">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h3>{group.productName}</h3>
                {isSuperUser && group.productId && (
                <button
                  type="button"
                    onClick={() => handleDeleteProduct(group.productId, group.productName)}
                    style={{
                      padding: '4px 8px',
                      fontSize: '0.875rem',
                      border: '1px solid #ef4444',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      color: '#ef4444',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    title="Delete product (super users only)"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                )}
                {isSuperUser && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const productName = (group.productName || '').trim();
                        if (!productName) {
                          alert('No product name to open costcard.');
                          return;
                        }
                        const token = localStorage.getItem('token');
                        const headers = { 'Content-Type': 'application/json' };
                        if (token) headers.Authorization = `Bearer ${token}`;

                        // Try inventory v2 lookup first
                        const primaryEndpoint = DEFAULT_BACKEND_BASE
                          ? `${DEFAULT_BACKEND_BASE}/api/inventory/v2/costcard-products?name=${encodeURIComponent(productName)}`
                          : `/api/inventory/v2/costcard-products?name=${encodeURIComponent(productName)}`;

                        let card = null;
                        try {
                          const response = await fetch(primaryEndpoint, { headers });
                          if (response.ok) {
                            const payload = await response.json();
                            const data = payload?.data || payload;
                            if (data && data._id && data.templateKey) {
                              card = data;
                            }
                          }
                        } catch (e) {
                          console.warn('Primary costcard lookup failed, will fallback', e);
                        }

                        // Fallback: fetch list of costcards and find best match (exact, then case-insensitive)
                        if (!card) {
                          const listEndpoint = DEFAULT_BACKEND_BASE
                            ? `${DEFAULT_BACKEND_BASE}/api/costcards`
                            : '/api/costcards';
                          const listResp = await fetch(listEndpoint, { headers });
                          if (listResp.ok) {
                            const list = await listResp.json();
                            const normalize = (name) => (name || '').toLowerCase().trim().replace(/\s+/g, ' ');
                            const target = normalize(productName);
                            card =
                              list.find(c => normalize(c.productName) === target) ||
                              list.find(c => normalize(c.productName).includes(target) || target.includes(normalize(c.productName)));
                          }
                        }

                        if (card && card._id && card.templateKey) {
                          const url = `/costcards/create/${card.templateKey}?id=${encodeURIComponent(card._id)}`;
                          window.open(url, '_blank', 'noopener,noreferrer');
                        } else {
                          // Last fallback: open list filtered by product
                          const encodedName = encodeURIComponent(productName);
                          window.open(`/costcards?product=${encodedName}`, '_blank', 'noopener,noreferrer');
                        }
                      } catch (error) {
                        console.error('Error opening costcard:', error);
                        const encodedName = encodeURIComponent(group.productName);
                        window.open(`/costcards?product=${encodedName}`, '_blank', 'noopener,noreferrer');
                      }
                  }}
                  style={{
                    padding: '4px 12px',
                    fontSize: '0.875rem',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  View Cost Card
                </button>
                )}
              </div>
              <p className="group-meta">
                {group.productCode || '—'}
              </p>
            </div>
            <span className="group-count">
              {group.rows.length} batch{group.rows.length === 1 ? '' : 'es'}
            </span>
          </header>
          <div className="table-container compact">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Batch no</th>
                  <th>Production date</th>
                  <th>Expiry date</th>
                  <th>Qty (kg)</th>
                  <th>Status</th>
                  <th>Country of Origin</th>
                  {isSuperUser && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {group.rows.map((row) => (
                  <tr key={row.id ?? row._id}>
                    <td>{row.batchNo}</td>
                    <td>{formatDate(row.productionDate)}</td>
                    <td>{formatDate(row.expiryDate)}</td>
                    <td>{formatNumber(row.currentQtyKg ?? 0)}</td>
                    <td>{renderStatusPill(row)}</td>
                    <td>{row.countryOfOrigin ?? '—'}</td>
                    {isSuperUser && (
                      <td>
                        <button
                          type="button"
                          onClick={() => {
                            const batchId = row.id ?? row._id;
                            const statusValue = row.status || 'Open';
                            console.log('📝 [Edit Batch] Initializing form with batch data:', {
                              batchId,
                              batchNo: row.batchNo,
                              countryOfOrigin: row.countryOfOrigin,
                              countryOfOriginType: typeof row.countryOfOrigin,
                              rowData: row
                            });
                            setEditBatchForm({
                              batchNo: row.batchNo || '',
                              productionDate: formatDateInputValue(row.productionDate),
                              expiryDate: formatDateInputValue(row.expiryDate),
                              status: statusValue,
                              countryOfOrigin: row.countryOfOrigin ?? '', // Use nullish coalescing to preserve empty strings
                              pfiOrPoNumber: row.pfiOrPoNumber || '',
                              attachments: row.attachments || []
                            });
                            setEditBatchError(null);
                            setEditBatchModal({
                              batchId,
                              productName: group.productName
                            });
                          }}
                          style={{
                            padding: '4px 8px',
                            fontSize: '0.75rem',
                            border: '1px solid #0ea5e9',
                            borderRadius: '4px',
                            backgroundColor: 'white',
                            color: '#0ea5e9',
                            cursor: 'pointer'
                          }}
                        >
                          Edit
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );

  const renderBatches = () => (
    <div className="tab-content">
      <header className="tab-header">
        <div>
          <h2>Batches</h2>
          <p>Detailed batch register for QA, finance, and traceability reviews.</p>
        </div>
      </header>

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="batches-search">Search</label>
          <input
            id="batches-search"
            type="text"
            placeholder="Search by product, code, batch, or country"
            value={batchesSearchTerm}
            onChange={(event) => setBatchesSearchTerm(event.target.value)}
          />
        </div>
      </div>

      <div className="table-container">
        <table className="inventory-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Batch no</th>
              <th>Production date</th>
              <th>Expiry date</th>
              <th>Opening qty (kg)</th>
              <th>Issued qty (kg)</th>
              <th>Current qty (kg)</th>
              <th>Country of Origin</th>
              <th>Status</th>
              <th>Created on</th>
              <th>Updated on</th>
              {isSuperUser && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {batchesWithProduct.map((batch) => (
              <tr key={batch.id ?? batch._id}>
                <td>{batch.productName}</td>
                <td>{batch.batchNo}</td>
                <td>{formatDate(batch.productionDate)}</td>
                <td>{formatDate(batch.expiryDate)}</td>
                <td>{formatNumber(batch.openingQtyKg ?? 0)}</td>
                <td>{formatNumber(batch.issuedQtyKg ?? 0)}</td>
                <td>{formatNumber(batch.currentQtyKg ?? 0)}</td>
                <td>{batch.countryOfOrigin ?? '—'}</td>
                <td>{renderStatusPill(batch)}</td>
                <td>{formatDate(batch.createdAt)}</td>
                <td>{formatDate(batch.updatedAt)}</td>
                {isSuperUser && (
                  <td>
                    <button
                      type="button"
                      onClick={() => handleDeleteBatch(
                        batch.id ?? batch._id,
                        batch.batchNo,
                        batch.productName
                      )}
                      style={{
                        padding: '4px 8px',
                        fontSize: '0.875rem',
                        border: '1px solid #ef4444',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        color: '#ef4444',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title="Delete batch (super users only)"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const filteredMovementsForSearch = useMemo(() => {
    if (!movementsSearchTerm || !movementsSearchTerm.trim()) {
      return filteredMovements;
    }

    const keyword = movementsSearchTerm.trim().toLowerCase();
    return filteredMovements.filter((movement) => {
      const productName = (movement.product?.productName || movement.productName || '').toString().toLowerCase();
      const productCode = (movement.product?.productCode || movement.productCode || '').toString().toLowerCase();
      const batchNo = (movement.batch?.batchNo || movement.batchNo || '').toString().toLowerCase();
      const reference = (movement.reference || '').toString().toLowerCase();
      const movementType = (movement.movementType || '').toString().toLowerCase();
      
      return (
        productName.includes(keyword) ||
        productCode.includes(keyword) ||
        batchNo.includes(keyword) ||
        reference.includes(keyword) ||
        movementType.includes(keyword)
      );
    });
  }, [filteredMovements, movementsSearchTerm]);

  const movementGroups = useMemo(() => {
    const map = new Map();

    filteredMovementsForSearch.forEach((movement) => {
      const productId =
        movement.product?.id ??
        movement.product?._id ??
        movement.productId ??
        movement.product ??
        null;

      if (!productId) {
        return;
      }

      if (!map.has(productId)) {
        map.set(productId, {
          productId,
          productName: movement.product?.productName ?? movement.productName ?? '—',
          hsCode: movement.product?.productCode ?? movement.productCode ?? '',
          movements: [],
          lastDate: movement.date ?? null,
          totals: {
            receipt: 0,
            issue: 0,
            adjustment: 0,
            netQty: 0
          }
        });
      }

      const entry = map.get(productId);
      entry.movements.push(movement);

      if (movement.date) {
        if (!entry.lastDate || new Date(movement.date) > new Date(entry.lastDate)) {
          entry.lastDate = movement.date;
        }
      }

      if (movement.movementType === 'RECEIPT') {
        entry.totals.receipt += 1;
      } else if (movement.movementType === 'ISSUE') {
        entry.totals.issue += 1;
      } else if (movement.movementType === 'ADJUSTMENT') {
        entry.totals.adjustment += 1;
      }

      entry.totals.netQty += movement.qtyKg ?? 0;
    });

    return Array.from(map.values())
      .map((entry) => ({
        ...entry,
        movements: entry.movements
          .slice()
          .sort((a, b) => new Date(b.date) - new Date(a.date))
      }))
      .sort((a, b) => new Date(b.lastDate || 0) - new Date(a.lastDate || 0));
  }, [filteredMovementsForSearch]);

  const [selectedMovementProductId, setSelectedMovementProductId] = useState(null);
  const [selectedMovementDetail, setSelectedMovementDetail] = useState(null);

  const selectedMovementGroup = useMemo(() => {
    if (!selectedMovementProductId) {
      return null;
    }
    return (
      movementGroups.find((group) => String(group.productId) === String(selectedMovementProductId)) || null
    );
  }, [movementGroups, selectedMovementProductId]);

  const incomingOrderFilterStatuses = useMemo(() => {
    const statusSet = new Set();
    // Add all standard status options
    INCOMING_ORDER_STATUS_OPTIONS.forEach(opt => statusSet.add(opt.value));
    // Add any additional statuses from existing orders (for backward compatibility)
    incomingOrders.forEach((order) => {
      if (order.status) {
        const normalized = normalizeStatus(order.status);
        statusSet.add(normalized);
      }
    });
    // Convert to array of {value, label} objects
    return Array.from(statusSet).map(statusValue => {
      const option = INCOMING_ORDER_STATUS_OPTIONS.find(opt => opt.value === statusValue);
      return option || { value: statusValue, label: statusValue };
    });
  }, [incomingOrders]);

  const renderMovements = () => (
    <div className="tab-content">
      <header className="tab-header">
        <div>
          <h2>Stock Movements</h2>
          <p>Complete movement history — mirrors the stock card sheets.</p>
        </div>
      </header>

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="movements-search">Search</label>
          <input
            id="movements-search"
            type="text"
            placeholder="Search by product, code, batch, or reference"
            value={movementsSearchTerm}
            onChange={(event) => setMovementsSearchTerm(event.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="movement-product">Product</label>
          <select
            id="movement-product"
            value={movementFilters.productId}
            onChange={(event) => handleMovementFilterChange('productId', event.target.value)}
          >
            <option value="all">All products</option>
            {inventoryProducts.map((product) => (
              <option key={product.id} value={product.id}>
                {product.productName}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="movement-start">From</label>
          <input
            id="movement-start"
            type="date"
            value={movementFilters.startDate}
            onChange={(event) => handleMovementFilterChange('startDate', event.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="movement-end">To</label>
          <input
            id="movement-end"
            type="date"
            value={movementFilters.endDate}
            onChange={(event) => handleMovementFilterChange('endDate', event.target.value)}
          />
        </div>
      </div>

      <div className="table-container">
        {movementGroups.length === 0 ? (
          <div className="empty-state">
            <p>No movements recorded yet. Receive, issue, or adjust stock to see history.</p>
          </div>
        ) : (
          <table className="inventory-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>HS Code</th>
                <th>Last Movement</th>
                <th>Receipts</th>
                <th>Issues</th>
                <th>Adjustments</th>
                <th>Net Qty (kg)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {movementGroups.map((group) => (
                <tr key={group.productId}>
                  <td>
                    <strong>{group.productName}</strong>
                  </td>
                  <td>{group.hsCode || '—'}</td>
                  <td>{formatDate(group.lastDate)}</td>
                  <td>{group.totals.receipt}</td>
                  <td>{group.totals.issue}</td>
                  <td>{group.totals.adjustment}</td>
                  <td className={group.totals.netQty < 0 ? 'negative' : group.totals.netQty > 0 ? 'positive' : ''}>
                    {formatNumber(group.totals.netQty)}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setSelectedMovementProductId(group.productId)}
                    >
                      View movement history
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedMovementGroup && (
        <div className="modal-overlay movement-detail-overlay" role="dialog" aria-modal="true">
          <div className="movement-detail-panel">
            <header className="movement-detail-header">
              <div>
                <h3>{selectedMovementGroup.productName}</h3>
                <p className="movement-meta">
                  {selectedMovementGroup.hsCode ? `HS code: ${selectedMovementGroup.hsCode}` : 'HS code: —'}
                </p>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={() => setSelectedMovementProductId(null)}
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <div className="movement-detail-body">
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Batch</th>
                    <th>Expiry</th>
                    <th>Type</th>
                    <th>Qty (kg)</th>
                    <th>Balance (kg)</th>
                    <th>PFI / PO Number</th>
                    <th>Processed By</th>
                    <th>Documents</th>
                    <th>Actions</th>
                    {isSuperUser && <th>Delete</th>}
                  </tr>
                </thead>
                <tbody>
                  {selectedMovementGroup.movements.map((movement) => {
                    const type = MOVEMENT_TYPES[movement.movementType] ?? {
                      label: movement.movementType,
                      tone: 'neutral'
                    };
                    const qtyClass = movement.qtyKg < 0 ? 'negative' : 'positive';
                    const expiryValue =
                      movement.batch?.expiryDate ?? movement.expiryDate ?? null;
                    const processedBy = movement.user?.name || movement.user?.email || '—';
                    // Ensure attachments is always an array for all movement types
                    const attachments = Array.isArray(movement.attachments) 
                      ? movement.attachments 
                      : (movement.attachments ? [movement.attachments] : []);
                    return (
                      <tr key={movement.id ?? movement._id}>
                          <td>{formatDate(movement.date)}</td>
                          <td>{movement.batch?.batchNo ?? movement.batchNo ?? '—'}</td>
                          <td>{formatDate(expiryValue)}</td>
                          <td>
                            <span className={`status-pill ${type.tone}`}>{type.label}</span>
                          </td>
                          <td className={qtyClass}>{formatNumber(movement.qtyKg)}</td>
                          <td>{formatNumber(movement.balanceKg ?? 0)}</td>
                          <td>
                            {movement.movementType === 'ADJUSTMENT' 
                              ? (movement.pfiOrPoNumber || movement.poNumber || '—')
                              : (movement.pfiOrPoNumber || movement.poNumber || movement.reference || '—')
                            }
                          </td>
                          <td>{processedBy}</td>
                          <td>
                            {attachments.length > 0 ? (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <FileText size={14} />
                                {attachments.length} file{attachments.length !== 1 ? 's' : ''}
                              </span>
                            ) : '—'}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="link-button"
                              onClick={() => setSelectedMovementDetail(movement)}
                            >
                              View Details
                            </button>
                          </td>
                          {isSuperUser && (
                            <td>
                              <button
                                type="button"
                                onClick={() => handleDeleteStockMovement(
                                  movement.id ?? movement._id,
                                  movement.movementType,
                                  formatDate(movement.date)
                                )}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '0.875rem',
                                  border: '1px solid #ef4444',
                                  borderRadius: '4px',
                                  backgroundColor: 'white',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                title="Delete stock movement (super users only)"
                              >
                                <Trash2 size={14} />
                                Delete
                              </button>
                            </td>
                          )}
                        </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <footer className="movement-detail-footer">
              <button
                type="button"
                className="action-btn secondary"
                onClick={() => setSelectedMovementProductId(null)}
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      )}

      {selectedMovementDetail && (
        <div className="modal-overlay movement-detail-overlay" role="dialog" aria-modal="true">
          <div className="movement-detail-panel" style={{ maxWidth: '800px' }}>
            <header className="movement-detail-header">
              <div>
                <h3>Movement Details</h3>
                <p className="movement-meta">
                  {selectedMovementDetail.movementType} - {formatDate(selectedMovementDetail.date)}
                </p>
              </div>
              <button
                type="button"
                className="modal-close"
                onClick={() => setSelectedMovementDetail(null)}
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <div className="movement-detail-body" style={{ padding: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <strong>Product:</strong> {selectedMovementDetail.product?.productName || '—'}
                </div>
                <div>
                  <strong>Batch:</strong> {selectedMovementDetail.batch?.batchNo || '—'}
                </div>
                <div>
                  <strong>Action Type:</strong> {MOVEMENT_TYPES[selectedMovementDetail.movementType]?.label || selectedMovementDetail.movementType}
                </div>
                <div>
                  <strong>Quantity:</strong> {formatNumber(selectedMovementDetail.qtyKg)} kg
                </div>
                {selectedMovementDetail.movementType === 'ADJUSTMENT' ? (
                  <>
                    <div>
                      <strong>Reason:</strong> {selectedMovementDetail.reference || '—'}
                    </div>
                    {(selectedMovementDetail.pfiOrPoNumber || selectedMovementDetail.poNumber) && (
                      <div>
                        <strong>PFI / PO Number:</strong> {selectedMovementDetail.pfiOrPoNumber || selectedMovementDetail.poNumber || '—'}
                      </div>
                    )}
                  </>
                ) : (
                <div>
                  <strong>PFI / PO Number:</strong> {selectedMovementDetail.pfiOrPoNumber || selectedMovementDetail.poNumber || selectedMovementDetail.reference || '—'}
                </div>
                )}
                <div>
                  <strong>Customer:</strong> {selectedMovementDetail.customer || '—'}
                </div>
                <div>
                  <strong>Processed By:</strong> {selectedMovementDetail.user?.name || selectedMovementDetail.user?.email || '—'}
                </div>
                <div>
                  <strong>Checked By:</strong> {
                    selectedMovementDetail.checkedBy 
                      ? (
                          typeof selectedMovementDetail.checkedBy === 'object' 
                            ? (selectedMovementDetail.checkedBy.name || selectedMovementDetail.checkedBy.email || '—')
                            : '—'
                        )
                      : '—'
                  }
                </div>
                {selectedMovementDetail.countryOfOrigin && (
                  <div>
                    <strong>Country of Origin:</strong> {selectedMovementDetail.countryOfOrigin}
                  </div>
                )}
                <div>
                  <strong>Expiry Date:</strong> {formatDate(selectedMovementDetail.batch?.expiryDate)}
                </div>
                <div>
                  <strong>Timestamp:</strong> {formatDate(selectedMovementDetail.date)}
                </div>
              </div>
              {selectedMovementDetail.remarks && (
                <div style={{ marginBottom: '24px' }}>
                  <strong>Remarks:</strong>
                  <p style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '4px' }}>
                    {selectedMovementDetail.remarks}
                  </p>
                </div>
              )}
              {(() => {
                const movementAttachments = Array.isArray(selectedMovementDetail.attachments) 
                  ? selectedMovementDetail.attachments 
                  : [];
                return movementAttachments.length > 0 && (
                <div>
                    <strong>Attachments ({movementAttachments.length}):</strong>
                  <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {movementAttachments.map((file, idx) => {
                      // Handle file viewing (matching WorkflowAutomations pattern)
                      const handleFileView = () => {
                        console.log('handleFileView called with file:', file);
                        let fileUrl = null;
                        
                        // Try different URL sources in order of preference
                        if (file.url) {
                          fileUrl = file.url;
                        } else if (file.path) {
                          fileUrl = file.path;
                        } else if (file.filename) {
                          fileUrl = `/uploads/chat/${file.filename}`;
                        }
                        
                        if (!fileUrl) {
                          console.error('No valid file URL found');
                          alert('Unable to view file: No valid URL found');
                          return;
                        }
                        
                        // Construct full URL if relative
                        const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${UPLOAD_API_BASE}${fileUrl.startsWith('/') ? fileUrl : '/' + fileUrl}`;
                        
                        console.log('Opening file URL:', fullUrl);
                        const newWindow = window.open(fullUrl, '_blank');
                        if (!newWindow) {
                          console.error('Failed to open new window - popup blocked?');
                          alert('Unable to open file. Please check if popups are blocked.');
                        }
                      };
                      
                      // Handle file download (matching WorkflowAutomations pattern)
                      const handleFileDownload = async () => {
                        try {
                          await downloadInventoryFile(file);
                        } catch (error) {
                          console.error('Unable to download file:', error);
                          alert('Unable to download file. Please try again.');
                        }
                      };
                      
                      return (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: '#f8fafc', borderRadius: '4px' }}>
                        <FileText size={16} />
                          <span style={{ flex: 1, fontSize: '0.875rem' }}>{file.originalName || file.filename || 'Attachment'}</span>
                          <button
                            type="button"
                            onClick={handleFileView}
                            style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '4px',
                              padding: '6px 12px', 
                              backgroundColor: '#3b82f6', 
                              color: 'white', 
                              border: 'none',
                              borderRadius: '4px', 
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: '500'
                            }}
                            title="View file in new tab"
                          >
                            <Eye size={14} />
                            View
                          </button>
                          <button
                            type="button"
                            onClick={handleFileDownload}
                            style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '4px',
                              padding: '6px 12px', 
                              backgroundColor: '#10b981', 
                              color: 'white', 
                              border: 'none',
                              borderRadius: '4px', 
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: '500'
                            }}
                            title="Download file"
                          >
                            <Download size={14} />
                            Download
                          </button>
                      </div>
                      );
                    })}
                  </div>
                </div>
              );
              })()}
            </div>
            <footer className="movement-detail-footer">
              <button
                type="button"
                className="action-btn secondary"
                onClick={() => setSelectedMovementDetail(null)}
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      )}
      {editBatchModal && renderEditBatchModal()}
    </div>
  );

  const renderIncoming = () => (
    <div className="tab-content">
      <header className="tab-header">
        <div>
          <h2>Incoming Orders</h2>
          <p>Track what is in transit so space can be prepared ahead of arrival.</p>
        </div>
        <button
          type="button"
          className="action-btn"
          onClick={() => {
            setModal(null);
            setTimeout(() => {
              setModal({ type: 'register-order' });
            }, 0);
          }}
        >
          <PlusCircle size={18} aria-hidden="true" />
          Register incoming order
        </button>
      </header>

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="incoming-orders-search">Search</label>
          <input
            id="incoming-orders-search"
            type="text"
            placeholder="Search by supplier, PFI, product, PO, BL, vessel, etc."
            value={incomingOrdersSearchTerm}
            onChange={(event) => setIncomingOrdersSearchTerm(event.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="incoming-status">Status</label>
          <select
            id="incoming-status"
            value={orderStatusFilter}
            onChange={(event) => setOrderStatusFilter(event.target.value)}
          >
            <option value="all">All statuses</option>
            {incomingOrderFilterStatuses.map((statusOption) => (
              <option key={statusOption.value} value={statusOption.value}>
                {statusOption.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="table-container">
        <table className="inventory-table">
          <thead>
            <tr>
              <th>SUPPLIER</th>
              <th>PFI NO.</th>
              <th>PRODUCT</th>
              <th>PO NO.</th>
              <th>QNTY</th>
              <th>SHIPPING ETD</th>
              <th>ETA MBSA</th>
              <th>BL</th>
              <th>VESSEL NAME</th>
              <th>POSITION</th>
              <th>STATUS</th>
              <th>Actions</th>
              {isSuperUser && <th>Delete</th>}
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order) => (
              <tr key={order.id ?? order._id}>
                <td>{order.supplier ?? '—'}</td>
                <td>{order.pfiNo ?? '—'}</td>
                <td>{order.product?.productName ?? order.productName ?? '—'}</td>
                <td>{order.poNo ?? '—'}</td>
                <td>
                  {formatNumber(order.qtyOrdered)} {order.unit}
                </td>
                <td>{formatDate(order.etd)}</td>
                <td>{order.eta ? formatDate(order.eta) : '—'}</td>
                <td>{order.bl ?? '—'}</td>
                <td>{order.vesselName ?? '—'}</td>
                <td>{order.position ?? '—'}</td>
                <td>
                  <span className={`status-pill ${getIncomingOrderStatusTone(order.status)}`}>
                    {getStatusLabel(order.status)}
                  </span>
                </td>
                <td>
                  {(() => {
                    const normalizedStatus = normalizeStatus(order.status);
                    const isReceived = normalizedStatus === 'Received';
                    
                    if (isReceived) {
                      return <span style={{ color: '#64748b', fontStyle: 'italic' }}>Order completed</span>;
                    }
                    
                    return (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => {
                            const normalizedStatus = normalizeStatus(order.status);
                            if (normalizedStatus === 'Received') {
                              alert('Cannot receive stock for an order that has already been fully received.');
                              return;
                            }
                            openReceiveStockForOrder(order);
                          }}
                        >
                          Receive stock
                        </button>
                        <span style={{ color: '#cbd5e1' }}>|</span>
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => {
                            const normalizedStatus = normalizeStatus(order.status);
                            if (normalizedStatus === 'Received') {
                              alert('Cannot update an order that has already been received.');
                              return;
                            }
                            setModal({
                              type: 'receive-order',
                              context: {
                                orderId: order.id ?? order._id,
                                order
                              }
                            });
                          }}
                        >
                          Update order
                        </button>
                      </div>
                    );
                  })()}
                </td>
                {isSuperUser && (
                  <td>
                    <button
                      type="button"
                      onClick={() => handleDeleteIncomingOrder(
                        order.id ?? order._id,
                        order.orderNo,
                        order.product?.productName ?? order.productName
                      )}
                      style={{
                        padding: '4px 8px',
                        fontSize: '0.875rem',
                        border: '1px solid #ef4444',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        color: '#ef4444',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title="Delete incoming order (super users only)"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const orderStatusOptions = useMemo(() => {
    const options = [...INCOMING_ORDER_STATUS_OPTIONS];
    if (modal?.type === 'receive-order') {
      const currentStatus = modal.context?.order?.status;
      if (currentStatus) {
        const normalizedStatus = normalizeStatus(currentStatus);
        // Check if current status is already in options
        const exists = options.some(opt => opt.value === normalizedStatus);
        if (!exists) {
          // Add current status as an option if it's not in the standard list
          options.unshift({ value: normalizedStatus, label: normalizedStatus });
        }
      }
    }
    return options;
  }, [modal]);

  const incomingOrderProductOptions = useMemo(() => {
    const options = [];
    const inventoryProductNames = new Set();
    const inventoryProductMap = new Map(); // Map normalized name to product

    // Add inventory products first
    inventoryProducts.forEach((product) => {
      const productName = product.productName;
      const normalizedName = normalizeProductName(productName);
      inventoryProductNames.add(normalizedName);
      inventoryProductMap.set(normalizedName, product);
      options.push({
        id: String(product.id),
        name: productName,
        hsCode: product.productCode || '',
        source: 'inventory',
        displayName: productName
      });
    });

    // Add costcard products - always include them even if similar names exist in inventory
    // CostCard products are separate entities and should be selectable
    // We'll handle exact matching when creating/receiving stock
    costCardProducts.forEach((card) => {
      const cardName = card.productName;
      // Use encodeCostCardKey to properly encode the product name in the ID
      const costCardId = `costcard:${encodeURIComponent(cardName)}`;
        options.push({
        id: costCardId,
          name: cardName,
          source: 'costcard',
          hsCode: card.hsCode || '',
          displayName: cardName
        });
    });

    options.push({
      id: 'new:new',
      name: 'Add new product…',
      hsCode: '',
      source: 'new',
      displayName: 'Add new product…'
    });

    return options;
  }, [inventoryProducts, costCardProducts]);

  return (
    <div className="inventory-management">
      <header className="inventory-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1>Inventory Management</h1>
          <p>
            Purpose-built for Nesim — digitises whiteboards, stock cards, and incoming order sheets.
          </p>
        </div>
        <button
          type="button"
          className="action-btn secondary"
          onClick={() => setExportModalOpen(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
        >
          <Download size={18} aria-hidden="true" />
          Export report
        </button>
      </header>

      <nav className="inventory-navigation" aria-label="Inventory navigation">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`nav-tab ${isActive ? 'active' : ''}`}
            >
              <Icon size={18} aria-hidden="true" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="inventory-content">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'current-stock' && renderCurrentStock()}
        {activeTab === 'batches' && renderBatches()}
        {activeTab === 'movements' && renderMovements()}
        {activeTab === 'incoming-orders' && renderIncoming()}
        </div>

      {modal && (modal.type === 'receive-stock' || modal.type === 'issue' || modal.type === 'adjust') && (
        <InventoryModal
          modal={modal}
          onClose={() => {
            clearActionError();
            setModal(null);
          }}
          inventoryProducts={inventoryProducts}
          costCardProducts={costCardProducts}
          issueProducts={issueProductSummary}
          batches={batchList}
          recommendedBatchResolver={recommendedBatchForProduct}
          onSubmit={handleModalSubmit}
          submitting={loading.action}
          errorMessage={errors.action}
          errorSuggestion={errors.actionSuggestion}
          onClearError={clearActionError}
          onSetError={(message) => setErrors((prev) => ({ ...prev, action: message }))}
          fetchCostCardDetails={fetchCostCardDetails}
        />
      )}

      {(modal?.type === 'receive-order' || modal?.type === 'register-order') && (
        <div className="modal-overlay movement-detail-overlay" role="dialog" aria-modal="true">
          <div className="movement-detail-panel">
            {(() => {
              const isNewOrder = modal.type === 'register-order';
              return (
                <header className="movement-detail-header">
                  <div>
                    <h3>{isNewOrder ? 'Register incoming order' : 'Manage incoming order'}</h3>
                    <p className="movement-meta">
                      {isNewOrder
                        ? 'Capture a shipment that is on the way so it can be tracked before arrival.'
                        : `Update status or record receipt for order ${modal.context?.order?.pfiNo || modal.context?.order?.poNo || modal.context?.orderId}`}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="modal-close"
                    onClick={() => setModal(null)}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </header>
              );
            })()}
            <div className="movement-detail-body">
              <div className="modal-form-grid">
                <div className="form-field">
                  <label htmlFor="incoming-order-supplier">SUPPLIER</label>
                  <input
                    id="incoming-order-supplier"
                    type="text"
                    value={incomingOrderForm.supplier}
                    onChange={(event) =>
                      setIncomingOrderForm((prev) => ({
                        ...prev,
                        supplier: event.target.value
                      }))
                    }
                    placeholder="e.g. ABC Suppliers Ltd"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="incoming-order-pfiNo">PFI NO.</label>
                  <input
                    id="incoming-order-pfiNo"
                    type="text"
                    value={incomingOrderForm.pfiNo}
                    onChange={(event) =>
                      setIncomingOrderForm((prev) => ({
                        ...prev,
                        pfiNo: event.target.value
                      }))
                    }
                    placeholder="e.g. PFI-2025-001"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="incoming-order-product">PRODUCT</label>
                  <ProductSelect
                    id="incoming-order-product"
                    value={incomingOrderForm.productId}
                    onChange={(productId) => {
                      const option = incomingOrderProductOptions.find((item) => String(item.id) === productId);
                      const selectedProductName = option?.name ?? option?.displayName ?? '';
                      
                      console.log('📦 Product selected in incoming order:', {
                        productId,
                        productName: selectedProductName,
                        optionSource: option?.source,
                        isCostCard: productId?.startsWith('costcard:'),
                        optionDetails: option ? {
                          id: option.id,
                          name: option.name,
                          displayName: option.displayName,
                          source: option.source
                        } : null
                      });
                      
                      setIncomingOrderForm((prev) => ({
                        ...prev,
                        productId,
                        productName: selectedProductName || prev.productName
                      }));
                    }}
                    options={incomingOrderProductOptions}
                    placeholder="Search product by name or HS code"
                    required
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="incoming-order-poNo">PO NO.</label>
                  <input
                    id="incoming-order-poNo"
                    type="text"
                    value={incomingOrderForm.poNo}
                    onChange={(event) =>
                      setIncomingOrderForm((prev) => ({
                        ...prev,
                        poNo: event.target.value
                      }))
                    }
                    placeholder="e.g. PO-2025-001"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="incoming-order-qty">QNTY</label>
                  <input
                    id="incoming-order-qty"
                    type="number"
                    min="0"
                    step="any"
                    value={incomingOrderForm.qtyOrdered}
                    onChange={(event) =>
                      setIncomingOrderForm((prev) => ({
                        ...prev,
                        qtyOrdered: event.target.value
                      }))
                    }
                    placeholder="e.g. 25"
                    required
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="incoming-order-unit">Unit</label>
                  <select
                    id="incoming-order-unit"
                    value={incomingOrderForm.unit}
                    onChange={(event) =>
                      setIncomingOrderForm((prev) => ({
                        ...prev,
                        unit: event.target.value
                      }))
                    }
                  >
                    {UNIT_OPTIONS.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-field">
                  <label htmlFor="incoming-order-etd">SHIPPING ETD</label>
                  <input
                    id="incoming-order-etd"
                    type="date"
                    value={incomingOrderForm.etd}
                    onChange={(event) =>
                      setIncomingOrderForm((prev) => ({
                        ...prev,
                        etd: event.target.value
                      }))
                    }
                    required
                  />
                  <small style={{ display: 'block', color: '#64748b', marginTop: '4px' }}>
                    When shipment leaves port of origin
                  </small>
                </div>

                <div className="form-field">
                  <label htmlFor="incoming-order-eta">ETA MBSA</label>
                  <input
                    id="incoming-order-eta"
                    type="date"
                    value={incomingOrderForm.eta}
                    onChange={(event) =>
                      setIncomingOrderForm((prev) => ({
                        ...prev,
                        eta: event.target.value
                      }))
                    }
                  />
                  <small style={{ display: 'block', color: '#64748b', marginTop: '4px' }}>
                    When shipment is expected to arrive at Mombasa
                  </small>
                </div>

                <div className="form-field">
                  <label htmlFor="incoming-order-bl">BL</label>
                    <input
                    id="incoming-order-bl"
                    type="text"
                    value={incomingOrderForm.bl || ''}
                      onChange={(event) =>
                        setIncomingOrderForm((prev) => ({
                          ...prev,
                        bl: event.target.value
                      }))
                    }
                    placeholder="e.g. BL-2025-001"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="incoming-order-vesselName">VESSEL NAME</label>
                  <input
                    id="incoming-order-vesselName"
                    type="text"
                    value={incomingOrderForm.vesselName}
                    onChange={(event) =>
                      setIncomingOrderForm((prev) => ({
                        ...prev,
                        vesselName: event.target.value
                      }))
                    }
                    placeholder="e.g. MV Container Ship"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="incoming-order-position">POSITION</label>
                  <input
                    id="incoming-order-position"
                    type="text"
                    value={incomingOrderForm.position}
                    onChange={(event) =>
                      setIncomingOrderForm((prev) => ({
                        ...prev,
                        position: event.target.value
                      }))
                    }
                    placeholder="e.g. At port, In transit, etc."
                  />
                </div>

                <div className="form-field">
                  <label>Attachments (Optional)</label>
                  <small style={{ display: 'block', color: '#64748b', marginBottom: '8px' }}>
                    Upload BL/AWBL, Packing List, Supplier Invoice, etc. (PDF, JPEG, PNG, DOCX)
                  </small>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <input
                      ref={incomingOrderFileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.docx"
                      onChange={async (e) => {
                        const newFiles = Array.from(e.target.files || []);
                        if (newFiles.length > 0) {
                          setUploadingIncomingOrderFiles(true);
                          // Clear any previous upload errors
                          setIncomingOrderFormError(null);
                          try {
                            const uploadedFiles = await uploadFilesWithAutoChunking({
                              files: newFiles,
                              context: 'documents'
                            });
                            setIncomingOrderAttachments(prev => [...prev, ...uploadedFiles]);
                            if (incomingOrderFileInputRef.current) incomingOrderFileInputRef.current.value = '';
                            // Clear any errors on success
                            setIncomingOrderFormError(null);
                          } catch (error) {
                            console.error('File upload error:', error);
                            let errorMessage = error.message || 'Failed to upload files';
                            
                            // Provide more helpful messages for rate limit errors
                            if (errorMessage.includes('rate limit') || errorMessage.includes('RATE_LIMIT')) {
                              errorMessage = 'Upload rate limit exceeded. The server allows 20 uploads per 5 minutes. Please wait 5 minutes before trying again, or try uploading a smaller file.';
                            }
                            
                            // Set error in form state to show in UI
                            setIncomingOrderFormError(`File upload failed: ${errorMessage}`);
                            // Also show alert for immediate feedback
                            alert(`Failed to upload files: ${errorMessage}`);
                          } finally {
                            setUploadingIncomingOrderFiles(false);
                          }
                        }
                      }}
                      disabled={uploadingIncomingOrderFiles}
                      style={{ display: 'none' }}
                      id="incoming-order-file-input"
                    />
                    <label
                      htmlFor="incoming-order-file-input"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '4px',
                        cursor: uploadingIncomingOrderFiles ? 'not-allowed' : 'pointer',
                        backgroundColor: uploadingIncomingOrderFiles ? '#f1f5f9' : 'white',
                        color: uploadingIncomingOrderFiles ? '#94a3b8' : '#334155'
                      }}
                    >
                      <Upload size={16} />
                      {uploadingIncomingOrderFiles ? 'Uploading...' : 'Upload Files'}
                    </label>
                    {incomingOrderAttachments.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
                        {incomingOrderAttachments.map((file, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', backgroundColor: '#f8fafc', borderRadius: '4px' }}>
                            <FileText size={14} />
                            <span style={{ flex: 1, fontSize: '0.875rem' }}>{file.originalName || file.filename}</span>
                            <button
                              type="button"
                              onClick={() => setIncomingOrderAttachments(prev => prev.filter((_, i) => i !== idx))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="incoming-order-status">Status</label>
                  <select
                    id="incoming-order-status"
                    value={incomingOrderForm.status}
                    onChange={(event) =>
                      setIncomingOrderForm((prev) => ({
                        ...prev,
                        status: event.target.value
                      }))
                    }
                  >
                    {orderStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-field wide">
                  <label htmlFor="incoming-order-remarks">Remarks</label>
                  <textarea
                    id="incoming-order-remarks"
                    rows={3}
                    value={incomingOrderForm.remarks}
                    onChange={(event) =>
                      setIncomingOrderForm((prev) => ({
                        ...prev,
                        remarks: event.target.value
                      }))
                    }
                    placeholder="Optional notes about this order"
                  />
                </div>
              </div>

              {incomingOrderFormError && <p className="form-error">{incomingOrderFormError}</p>}
            </div>
            <footer className="movement-detail-footer">
              {modal.type === 'receive-order' && (() => {
                const orderStatus = normalizeStatus(modal.context?.order?.status);
                const isReceived = orderStatus === 'Received';
                
                if (isReceived) {
                  return null; // Don't show "Record stock receipt" button for received orders
                }
                
                return (
                  <button
                    type="button"
                    className="action-btn secondary"
                    onClick={() => openReceiveStockForOrder(modal.context?.order)}
                  >
                    Record stock receipt
                  </button>
                );
              })()}
              <button
                type="button"
                className="action-btn"
                onClick={handleIncomingOrderSave}
                disabled={incomingOrderFormSaving}
              >
                {incomingOrderFormSaving
                  ? 'Saving…'
                  : modal.type === 'register-order'
                    ? 'Register order'
                    : 'Save order'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {editBatchModal && renderEditBatchModal()}

      {exportModalOpen && (
        <div className="modal-overlay movement-detail-overlay" role="dialog" aria-modal="true" aria-labelledby="export-modal-title">
          <div className="movement-detail-panel" style={{ maxWidth: '480px' }}>
            <header className="movement-detail-header">
              <div>
                <h3 id="export-modal-title">Export report</h3>
                <p className="movement-meta">Choose what to export. Full pack includes all sheets in one Excel file.</p>
              </div>
              <button type="button" className="modal-close" onClick={() => setExportModalOpen(false)} aria-label="Close">×</button>
            </header>
            <div className="movement-detail-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportSelections.fullPack}
                    onChange={(e) => setExportSelections((prev) => ({ ...prev, fullPack: e.target.checked }))}
                  />
                  <strong>Full Inventory Pack (All Sheets)</strong>
                </label>
                {!exportSelections.fullPack && (
                  <>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={exportSelections.currentStockSummary}
                        onChange={(e) => setExportSelections((prev) => ({ ...prev, currentStockSummary: e.target.checked }))}
                      />
                      Current Stock Summary
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={exportSelections.batchRegister}
                        onChange={(e) => setExportSelections((prev) => ({ ...prev, batchRegister: e.target.checked }))}
                      />
                      Batch Register (Detailed)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={exportSelections.stockMovement}
                        onChange={(e) => setExportSelections((prev) => ({ ...prev, stockMovement: e.target.checked }))}
                      />
                      Stock Movement Report
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={exportSelections.incomingOrders}
                        onChange={(e) => setExportSelections((prev) => ({ ...prev, incomingOrders: e.target.checked }))}
                      />
                      Incoming Orders Report
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={exportSelections.riskExpiry}
                        onChange={(e) => setExportSelections((prev) => ({ ...prev, riskExpiry: e.target.checked }))}
                      />
                      Expiring &amp; Risk Report
                    </label>
                  </>
                )}
                {exportSelections.stockMovement && !exportSelections.fullPack && (
                  <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <strong style={{ fontSize: '0.875rem' }}>Filters for Stock Movement Report</strong>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <div>
                        <label htmlFor="export-start-date" style={{ fontSize: '0.75rem', display: 'block', marginBottom: '4px' }}>From date</label>
                        <input
                          id="export-start-date"
                          type="date"
                          value={exportFilters.startDate}
                          onChange={(e) => setExportFilters((prev) => ({ ...prev, startDate: e.target.value }))}
                          style={{ width: '100%', padding: '6px 8px' }}
                        />
                      </div>
                      <div>
                        <label htmlFor="export-end-date" style={{ fontSize: '0.75rem', display: 'block', marginBottom: '4px' }}>To date</label>
                        <input
                          id="export-end-date"
                          type="date"
                          value={exportFilters.endDate}
                          onChange={(e) => setExportFilters((prev) => ({ ...prev, endDate: e.target.value }))}
                          style={{ width: '100%', padding: '6px 8px' }}
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="export-product" style={{ fontSize: '0.75rem', display: 'block', marginBottom: '4px' }}>Product</label>
                      <select
                        id="export-product"
                        value={exportFilters.productId}
                        onChange={(e) => setExportFilters((prev) => ({ ...prev, productId: e.target.value }))}
                        style={{ width: '100%', padding: '6px 8px' }}
                      >
                        <option value="all">All products</option>
                        {inventoryProducts.map((p) => (
                          <option key={p.id ?? p._id} value={p.id ?? p._id}>{p.productName}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="export-movement-type" style={{ fontSize: '0.75rem', display: 'block', marginBottom: '4px' }}>Movement type</label>
                      <select
                        id="export-movement-type"
                        value={exportFilters.movementType}
                        onChange={(e) => setExportFilters((prev) => ({ ...prev, movementType: e.target.value }))}
                        style={{ width: '100%', padding: '6px 8px' }}
                      >
                        <option value="all">All</option>
                        <option value="RECEIPT">Receive</option>
                        <option value="ISSUE">Issue</option>
                        <option value="ADJUSTMENT">Adjust</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <footer className="movement-detail-footer">
              <button type="button" className="action-btn secondary" onClick={() => setExportModalOpen(false)}>
                Cancel
              </button>
              <button type="button" className="action-btn" onClick={handleExportReport} disabled={exportLoading}>
                {exportLoading ? 'Exporting…' : 'Export to Excel'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

const DashboardCard = ({ icon: Icon, tone, title, value, subtitle }) => (
  <article className="stat-card">
    <div className={`stat-icon ${tone}`}>
      <Icon size={26} aria-hidden="true" />
    </div>
    <div className="stat-content">
      <h3>{formatNumber(value)}</h3>
      <p>{title}</p>
      <small>{subtitle}</small>
    </div>
  </article>
);

const AttentionBlock = ({ title, badge, description, actionLabel, onClick }) => (
  <div className="attention-block">
    <header>
      <h4>{title}</h4>
      <span className="badge">{badge}</span>
    </header>
    <p>{description}</p>
    <button type="button" className="link-button" onClick={onClick}>
      {actionLabel}
    </button>
  </div>
);

// ProductSelect - Searchable combobox component
const ProductSelect = ({ 
  value, 
  onChange, 
  options, 
  placeholder = "Search product by name or HS code",
  id,
  required = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Find selected product
  const selectedProduct = options.find(opt => opt.id === value);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) {
      return options;
    }
    const searchLower = searchTerm.toLowerCase();
    return options.filter(opt =>
      opt.name.toLowerCase().includes(searchLower) ||
      (opt.hsCode && opt.hsCode.toLowerCase().includes(searchLower))
    );
  }, [options, searchTerm]);

  // Debounce search for large lists
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 200);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const debouncedFilteredOptions = useMemo(() => {
    if (!debouncedSearch.trim()) {
      return options;
    }
    const searchLower = debouncedSearch.toLowerCase();
    return options.filter(opt =>
      opt.name.toLowerCase().includes(searchLower) ||
      (opt.hsCode && opt.hsCode.toLowerCase().includes(searchLower))
    );
  }, [options, debouncedSearch]);

  const displayOptions = options.length > 200 ? debouncedFilteredOptions : filteredOptions;

  // Handle selection
  const handleSelect = (optionId) => {
    onChange(optionId);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen && e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const newIndex = prev < displayOptions.length - 1 ? prev + 1 : prev;
          // Scroll into view if needed
          if (dropdownRef.current && newIndex >= 0) {
            const optionElement = dropdownRef.current.children[newIndex];
            if (optionElement) {
              optionElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : -1;
          // Scroll into view if needed
          if (dropdownRef.current && newIndex >= 0) {
            const optionElement = dropdownRef.current.children[newIndex];
            if (optionElement) {
              optionElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && displayOptions[highlightedIndex]) {
          handleSelect(displayOptions[highlightedIndex].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Display value
  const displayValue = isOpen && searchTerm 
    ? searchTerm 
    : (selectedProduct ? selectedProduct.displayName : '');

  return (
    <div className="product-select-container">
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        className="product-select-input"
        autoComplete="off"
      />
      {isOpen && displayOptions.length > 0 && (
        <div
          ref={dropdownRef}
          className="product-select-dropdown"
        >
          {displayOptions.map((option, index) => (
            <div
              key={option.id}
              onClick={() => handleSelect(option.id)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className="product-select-option"
              style={{
                backgroundColor: highlightedIndex === index ? '#f1f5f9' : 'white',
                borderBottom: index < displayOptions.length - 1 ? '1px solid #f1f5f9' : 'none'
              }}
            >
              <div className="product-select-option-name">
                {option.displayName}
              </div>
              {option.hsCode && (
                <div className="product-select-option-hscode">
                  HS Code: {option.hsCode}
                </div>
              )}
              {option.source === 'inventory' && (
                <div className="product-select-option-source">
                  Inventory Product
                </div>
              )}
              {option.source === 'costcard' && (
                <div className="product-select-option-source">
                  Cost Card Product
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {isOpen && displayOptions.length === 0 && (
        <div className="product-select-empty">
          No products found. Try another name or HS code.
        </div>
      )}
    </div>
  );
};

const InventoryModal = ({
  modal,
  onClose,
  inventoryProducts,
  costCardProducts,
  issueProducts,
  batches,
  recommendedBatchResolver,
  onSubmit,
  submitting,
  errorMessage,
  errorSuggestion,
  onClearError,
  onSetError,
  fetchCostCardDetails
}) => {
  const { type, context = {} } = modal;
  // Ensure sourceContext defaults to 'manual' for backward compatibility
  const sourceContext = context.sourceContext || 'manual';
  const linkedOrderId = context.linkedOrderId || context.orderId || null;

  const encodeCostCardKey = (name) => encodeURIComponent(name || '');
  const decodeCostCardKey = (key) => decodeURIComponent(key || '');

  const normalizedType = type === 'receive-stock' ? 'receive' : type;

  // Build filtered inventory products list
  const inventoryOptionList = useMemo(() => {
    if (normalizedType === 'issue' || normalizedType === 'adjust') {
      const allowedIds = new Set(
        issueProducts.map((product) => String(product.productId))
      );
      return inventoryProducts.filter((product) => allowedIds.has(String(product.id)));
    }
    return inventoryProducts;
  }, [inventoryProducts, issueProducts, normalizedType]);

  // Build flat product options list for searchable select
  const productOptions = useMemo(() => {
    const options = [];
    
    // Compute inventory products list inline to avoid dependency issues
    let filteredInventoryProducts = inventoryProducts;
    if (normalizedType === 'issue' || normalizedType === 'adjust') {
      const allowedIds = new Set(
        issueProducts.map((product) => String(product.productId))
      );
      filteredInventoryProducts = inventoryProducts.filter((product) => allowedIds.has(String(product.id)));
    }
    
    // Add inventory products
    filteredInventoryProducts.forEach((product) => {
      options.push({
        id: `inventory:${product.id}`,
        name: product.productName,
        hsCode: product.productCode || '',
        source: 'inventory',
        displayName: product.productName
      });
    });
    
    // Add cost card products (only for receive)
    // IMPORTANT: Always include CostCard products even if they exist in inventory
    // because they are separate products and should be selectable
    if (normalizedType === 'receive') {
      costCardProducts.forEach((product) => {
        // Use exact product name matching - don't filter by normalized name
        // CostCard products are separate from inventory products even if names are similar
        const costCardId = `costcard:${encodeCostCardKey(product.productName)}`;
        options.push({
          id: costCardId,
          name: product.productName,
          hsCode: product.hsCode || '',
          source: 'costcard',
          displayName: product.productName
        });
      });
      
      // Add "Create new product" option
      options.push({
        id: 'new:new',
        name: 'Create new product…',
        hsCode: '',
        source: 'new',
        displayName: 'Create new product…'
      });
    }
    
    return options;
  }, [inventoryProducts, issueProducts, costCardProducts, normalizedType]);

  const buildSelectionForContext = () => {
    if (context.productId) {
      return `inventory:${context.productId}`;
    }

    if (normalizedType === 'issue' || normalizedType === 'adjust') {
      if (issueProducts.length === 1) {
        return `inventory:${issueProducts[0].productId}`;
      }
      return '';
    }

    if (context.productSource === 'new') {
      return 'new:new';
    }

    if (
      context.productName &&
      costCardProducts.some((item) => item.productName === context.productName)
    ) {
      return `costcard:${encodeCostCardKey(context.productName)}`;
    }

    return '';
  };

  const buildProductState = (selectionValue) => {
    const [source, raw] = (selectionValue || '').split(':');

    if (!selectionValue) {
      return {
        productSelection: '',
        productSource: normalizedType === 'receive' ? 'new' : 'inventory',
        productId: '',
        productName: '',
        hsCode: '',
        productUnit: 'kg',
        productStandardPack: ''
      };
    }

    if (source === 'inventory') {
      // Find product by exact ID match - this ensures exact mapping
      const product = inventoryProducts.find((item) => {
        const itemId = String(item.id || item._id || '');
        const rawId = String(raw || '');
        return itemId === rawId;
      });
      
      const issueProduct = issueProducts.find(
        (item) => String(item.productId) === String(raw)
      );

      // Validate that we found the product - if not, log warning
      if (!product && raw) {
        console.warn('⚠️ Product not found in inventoryProducts for ID:', raw, {
          availableIds: inventoryProducts.map(p => String(p.id || p._id)).slice(0, 5),
          selectionValue
        });
      }

      // Ensure we use the exact product ID from the found product
      const exactProductId = product ? (product.id || product._id) : '';
      const exactProductName = product?.productName || issueProduct?.productName || '';

      // Log product selection for debugging
      if (product && exactProductId) {
        console.log('✅ Inventory product selected:', {
          productId: exactProductId,
          productName: exactProductName,
          selectionValue,
          rawId: raw,
          source: 'inventory'
        });
      }

      return {
        productSelection: selectionValue,
        productSource: 'inventory',
        productId: exactProductId,
        productName: exactProductName,
        hsCode: product?.productCode || issueProduct?.hsCode || '',
        productUnit: product?.unit || 'kg',
        productStandardPack:
          product?.standardPack == null || Number.isNaN(product.standardPack)
            ? ''
            : Number(product.standardPack)
      };
    }

    if (source === 'costcard') {
      if (normalizedType === 'issue' || normalizedType === 'adjust') {
        return buildProductState('');
      }
      const name = decodeCostCardKey(raw);
      // Find CostCard product by EXACT name match - no normalization
      const costCard = costCardProducts.find((item) => {
        // First try exact match
        if (item.productName === name) {
          return true;
        }
        // Then try case-insensitive exact match (trimmed)
        return item.productName?.trim().toLowerCase() === name?.trim().toLowerCase();
      });

      // CRITICAL: Do NOT match CostCard products to inventory products here
      // CostCard products are separate entities and should remain as CostCard selections
      // Matching will happen later in handleReceiveSubmit when creating/receiving stock

      // Log for debugging
      if (!costCard) {
        console.warn('⚠️ CostCard product not found:', {
          requestedName: name,
          rawSelection: raw,
          selectionValue,
          availableNames: costCardProducts.map(cp => cp.productName).slice(0, 5)
        });
      } else {
        console.log('✅ CostCard product found (keeping as CostCard):', {
          requestedName: name,
          foundName: costCard.productName,
          hsCode: costCard.hsCode,
          selectionValue,
          willRemainAsCostCard: true
        });
      }

      // IMPORTANT: Return CostCard source, NOT inventory
      // Do NOT try to match to inventory products here
      return {
        productSelection: selectionValue, // Keep the original CostCard selection
        productSource: 'costcard', // Must remain as 'costcard'
        productId: '', // CostCard products don't have inventory productId yet
        productName: name || '', // Use the exact name from selection
        hsCode: normaliseHsCode(costCard?.hsCode || ''),
        productUnit: 'kg',
        productStandardPack: ''
      };
    }

    return {
      productSelection: 'new:new',
      productSource: 'new',
      productId: '',
      productName: '',
      hsCode: '',
      productUnit: 'kg',
      productStandardPack: ''
    };
  };

  const [productSelection, setProductSelection] = useState(buildSelectionForContext);

  const [formState, setFormState] = useState(() => ({
    ...buildProductState(productSelection),
    batchId: '',
    batchNo: context.batchNo || '',
    productionDate: '',
    expiryDate: '',
    qty: context.qty ? String(context.qty) : '',
    reference: context.reference || '',
    pfiOrPoNumber: context.pfiOrPoNumber || context.reference || context.orderNo || '',
    poNumber: context.poNumber || '',
    remarks: '',
    countryOfOrigin: context.countryOfOrigin || '',
    issueTo: '',
    customer: context.customer || '',
    reason: '',
    newQuantity: '',
    checkedBy: context.checkedBy || '',
    orderId: (sourceContext === 'incoming-order' && linkedOrderId) ? linkedOrderId : (context.orderId || '')
  }));

  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Filter users to exclude standard users for checkedBy dropdown
  const filteredUsersForCheckedBy = useMemo(() => {
    const allowedRoles = ['admin', 'super', 'procurement', 'finance', 'store'];
    return users.filter(user => 
      user.role && allowedRoles.includes(user.role)
    );
  }, [users]);

  useEffect(() => {
    // Load users for issue, receive, and adjust (for checkedBy field)
    if (normalizedType === 'issue' || normalizedType === 'receive' || normalizedType === 'adjust') {
      setUsersLoading(true);
      const token = localStorage.getItem('token');
      fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
        .then(res => res.json())
        .then(data => {
          const usersList = Array.isArray(data) ? data : (data.users || []);
          // Filter out standard users - only show admin, super, procurement, finance, store roles
          const allowedRoles = ['admin', 'super', 'procurement', 'finance', 'store'];
          const filteredUsers = usersList.filter(user => 
            user.role && allowedRoles.includes(user.role)
          );
          setUsers(filteredUsers);
        })
        .catch(err => {
          console.error('Error loading users:', err);
          setUsers([]);
        })
        .finally(() => setUsersLoading(false));
    }
  }, [normalizedType]);

  const [localError, setLocalError] = useState(null);
  const [appliedSuggestion, setAppliedSuggestion] = useState(null);

  useEffect(() => {
    setProductSelection((prev) => {
      if (!prev) {
        return buildSelectionForContext();
      }
      const [source, raw] = prev.split(':');
      if (source === 'inventory') {
        if (inventoryProducts.some((product) => String(product.id) === raw)) {
          return prev;
        }
      } else if (source === 'costcard') {
        if (
          costCardProducts.some(
            (product) => encodeCostCardKey(product.productName) === raw
          )
        ) {
          return prev;
        }
      } else {
        return prev;
      }
      return buildSelectionForContext();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryProducts, costCardProducts, modal]);

  useEffect(() => {
    setFormState((prev) => {
      const productState = buildProductState(productSelection);
      // Preserve countryOfOrigin and other non-product fields when product selection changes
      return {
      ...prev,
        ...productState,
        countryOfOrigin: prev.countryOfOrigin || '', // Preserve countryOfOrigin
        batchNo: prev.batchNo || '', // Preserve batchNo
        productionDate: prev.productionDate || '', // Preserve productionDate
        expiryDate: prev.expiryDate || '', // Preserve expiryDate
        qty: prev.qty || '', // Preserve qty
        reference: prev.reference || '', // Preserve reference
        remarks: prev.remarks || '', // Preserve remarks
        checkedBy: prev.checkedBy || '' // Preserve checkedBy
      };
    });
    setLocalError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSelection]);

  useEffect(() => {
    const [source, raw] = (productSelection || '').split(':');
    if (source !== 'costcard') {
      return;
    }

    const name = decodeCostCardKey(raw);
    if (!name) {
      return;
    }

    let isMounted = true;

    fetchCostCardDetails(name)
      ?.then((details) => {
        if (!details || !isMounted) {
          return;
        }

        setFormState((prev) => ({
          ...prev,
          hsCode: details.hsCode ? normaliseHsCode(details.hsCode) : prev.hsCode,
          productUnit: details.unit || prev.productUnit,
          productStandardPack:
            details.standardPack == null || Number.isNaN(details.standardPack)
              ? prev.productStandardPack
              : Number(details.standardPack)
        }));
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSelection]);

  useEffect(() => {
    if (errorSuggestion) {
      setAppliedSuggestion(null);
    }
  }, [errorSuggestion]);

  useEffect(() => {
    if (appliedSuggestion && formState.batchNo !== appliedSuggestion) {
      setAppliedSuggestion(null);
    }
  }, [appliedSuggestion, formState.batchNo]);

  const filteredBatches = useMemo(() => {
    if (!formState.productId) {
      return [];
    }

    return batches.filter((batch) => {
      const batchProductId =
        batch.product?.id ?? batch.product?._id ?? batch.productId ?? batch.product ?? '';
      return String(batchProductId) === String(formState.productId);
    });
  }, [batches, formState.productId]);

  const recommended = useMemo(() => {
    if (normalizedType !== 'issue' || formState.productSource !== 'inventory' || !formState.productId) {
      return null;
    }
    return recommendedBatchResolver(formState.productId);
  }, [normalizedType, formState.productSource, formState.productId, recommendedBatchResolver]);

  useEffect(() => {
    if (normalizedType === 'issue' && recommended && !formState.batchId) {
      setFormState((prev) => ({
        ...prev,
        batchId: recommended.id ?? recommended._id ?? ''
      }));
    }
  }, [normalizedType, recommended, formState.batchId]);

  useEffect(() => {
    if (normalizedType === 'adjust' && filteredBatches.length > 0) {
      if (!filteredBatches.some((batch) => (batch.id ?? batch._id) === formState.batchId)) {
        const first = filteredBatches[0];
        setFormState((prev) => ({
          ...prev,
          batchId: first.id ?? first._id
        }));
      }
    }
  }, [filteredBatches, formState.batchId, normalizedType]);

  const handleChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNumberChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value === '' ? '' : Number(value)
    }));
  };

  const handleProductSelectionChange = (value) => {
    setProductSelection(value);
  };

  const applyBatchSuggestion = () => {
    if (!errorSuggestion) {
      return;
    }
    handleChange('batchNo', errorSuggestion);
    setLocalError(null);
    setAppliedSuggestion(errorSuggestion);
    onClearError?.();
  };

  const titleMap = {
    'receive-stock': 'Receive stock',
    receive: 'Receive stock',
    issue: 'Issue stock',
    adjust: 'Adjust stock balance'
  };

  // Reset attachments when modal type changes
  useEffect(() => {
    setAttachments([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [type]);

  useEffect(() => {
    if (normalizedType === 'receive') {
      // Handle incoming order context
      if (sourceContext === 'incoming-order' && context.productId) {
        // Pre-fill product (locked when from incoming order)
        if (!formState.productId) {
          setProductSelection(`inventory:${context.productId}`);
        }
      } else if (context.productName && !formState.productName) {
        setFormState((prev) => ({
          ...prev,
          productName: context.productName
        }));
      }
      
      // Pre-fill quantity from order
      if (context.qty && !formState.qty) {
        setFormState((prev) => ({
          ...prev,
          qty: String(context.qty)
        }));
      }
      
      // Pre-fill product selection
      if (context.productId && !formState.productId && sourceContext !== 'incoming-order') {
        setProductSelection(`inventory:${context.productId}`);
      }
      
      // Pre-fill PFI/PO Number from order
      if ((context.orderNo || context.reference) && !formState.pfiOrPoNumber) {
        setFormState((prev) => ({
          ...prev,
          pfiOrPoNumber: context.orderNo || context.reference || ''
        }));
      }
    }
  }, [
    normalizedType,
    sourceContext,
    context,
    formState.productId,
    formState.productName,
    formState.qty,
    formState.pfiOrPoNumber
  ]);

  const handleSubmit = async (event, modalType) => {
    event.preventDefault();
    setLocalError(null);

    if ((normalizedType === 'issue' || normalizedType === 'adjust') && !formState.productId) {
      setLocalError('Select a product to continue.');
      return;
    }

    if (
      normalizedType === 'receive' &&
      (formState.productSource === 'costcard' || formState.productSource === 'new') &&
      (!formState.productName || !formState.hsCode || !formState.productUnit)
    ) {
      setLocalError('Provide product name, HS code, and unit to create the product.');
      return;
    }

    // Ensure productId is set when receiving from incoming order
    let finalProductId = formState.productId;
    if (sourceContext === 'incoming-order' && context.productId && !finalProductId) {
      finalProductId = context.productId;
    }

    const payload = {
      ...formState,
      productId: finalProductId,
      hsCode: normaliseHsCode(formState.hsCode),
      attachments: attachments,
      ...(sourceContext === 'incoming-order' && linkedOrderId
        ? { orderId: linkedOrderId }
        : {})
    };

    const success = await onSubmit(type, payload);
    if (success) {
      onClose();
    }
  };

  const renderReceiveFields = () => (
    <>
      {context.order && (
        <div className="order-detail-grid">
          <div>
            <span>Inv / Order no.</span>
            <strong>{context.order.orderNo || '—'}</strong>
          </div>
          <div>
            <span>Product</span>
            <strong>{context.order.product?.productName ?? context.order.productName ?? '—'}</strong>
          </div>
          <div>
            <span>Quantity ordered</span>
            <strong>
              {formatNumber(context.order.qtyOrdered ?? formState.qty ?? 0)}{' '}
              {context.order.unit ?? formState.productUnit ?? 'kg'}
            </strong>
          </div>
          <div>
            <span>ETD</span>
            <strong>{formatDate(context.order.etd)}</strong>
          </div>
        </div>
      )}

      <div className="form-field">
        <label htmlFor={`${type}-product`}>Product</label>
        {normalizedType === 'receive' && sourceContext === 'incoming-order' ? (
          // Locked product field when receiving from incoming order
          <input
            id={`${type}-product`}
            type="text"
            value={context.order?.product?.productName ?? context.productName ?? ''}
            readOnly
            disabled
            className="form-input"
            style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed' }}
          />
        ) : normalizedType === 'receive' ? (
          <ProductSelect
            id={`${type}-product`}
            value={productSelection}
            onChange={handleProductSelectionChange}
            options={productOptions}
            placeholder="Search product by name or HS code"
            required
          />
        ) : (
          <select
            id={`${type}-product`}
            value={productSelection}
            onChange={(event) => handleProductSelectionChange(event.target.value)}
            required
          >
            <option value="" disabled>
              Select product
            </option>
            {inventoryOptionList && inventoryOptionList.length > 0 && (
              <optgroup label="Inventory products">
                {inventoryOptionList.map((product) => (
                  <option key={product.id} value={`inventory:${product.id}`}>
                    {product.productName}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        )}
      </div>

      {/* Show orderId field only when receiving from incoming order */}
      {normalizedType === 'receive' && sourceContext === 'incoming-order' && linkedOrderId && (
        <div className="form-field">
          <label htmlFor={`${type}-orderId`}>Order ID</label>
          <input
            id={`${type}-orderId`}
            type="text"
            value={linkedOrderId}
            readOnly
            disabled
            className="form-input"
            style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed' }}
          />
        </div>
      )}

      {normalizedType === 'issue' && issueProducts.length > 1 && (
        <div className="form-field wide">
          <div className="issue-product-list">
            <table className="issue-product-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>HS code</th>
                  <th>Available qty (kg)</th>
                  <th>Batches</th>
                </tr>
              </thead>
              <tbody>
                {issueProducts.map((product) => {
                  const isSelected =
                    formState.productId &&
                    String(formState.productId) === String(product.productId);
                  return (
                    <tr
                      key={product.productId}
                      className={`issue-product-row ${isSelected ? 'selected' : ''}`}
                      onClick={() =>
                        handleProductSelectionChange(`inventory:${product.productId}`)
                      }
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleProductSelectionChange(`inventory:${product.productId}`);
                        }
                      }}
                    >
                      <td>{product.productName}</td>
                      <td>{product.hsCode || '—'}</td>
                      <td>{formatNumber(product.totalQty ?? 0)}</td>
                      <td>
                        {product.batches && product.batches.length > 0
                          ? product.batches
                              .map((batch) =>
                                `${batch.batchNo} (${formatNumber(batch.currentQtyKg ?? 0)} kg)`
                              )
                              .join(', ')
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Show orderId field only when receiving from incoming order */}
      {normalizedType === 'receive' && sourceContext === 'incoming-order' && linkedOrderId && (
        <div className="form-field">
          <label htmlFor={`${type}-orderId`}>Order ID</label>
          <input
            id={`${type}-orderId`}
            type="text"
            value={linkedOrderId}
            readOnly
            disabled
            className="form-input"
            style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed' }}
          />
        </div>
      )}

      <div className="form-field">
        <label htmlFor={`${type}-batchNo`}>Batch no.</label>
        <input
          id={`${type}-batchNo`}
          type="text"
          value={formState.batchNo}
          onChange={(event) => handleChange('batchNo', event.target.value)}
          placeholder="e.g. 2023/001"
          required
        />
      </div>

      <div className="form-field">
        <label htmlFor={`${type}-productionDate`}>Production date</label>
        <input
          id={`${type}-productionDate`}
          type="date"
          value={formState.productionDate}
          onChange={(event) => handleChange('productionDate', event.target.value)}
          required
        />
      </div>

      <div className="form-field">
        <label htmlFor={`${type}-expiryDate`}>Expiry date</label>
        <input
          id={`${type}-expiryDate`}
          type="date"
          value={formState.expiryDate}
          onChange={(event) => handleChange('expiryDate', event.target.value)}
          required
        />
      </div>

      <div className="form-field">
        <label htmlFor={`${type}-qty`}>Quantity (kg)</label>
        <input
          id={`${type}-qty`}
          type="number"
          min="0"
          step="any"
          value={formState.qty}
          onChange={(event) => handleNumberChange('qty', event.target.value)}
          placeholder={sourceContext === 'incoming-order' && context.order?.qtyOrdered 
            ? `Ordered: ${formatNumber(context.order.qtyOrdered)}` 
            : "e.g. 100"}
          required
        />
        {sourceContext === 'incoming-order' && context.order?.qtyOrdered && (
          <small style={{ color: '#64748b', marginTop: '4px', display: 'block' }}>
            Total ordered: {formatNumber(context.order.qtyOrdered)} {context.order.unit || 'kg'}
          </small>
        )}
      </div>

      {normalizedType === 'receive' && (
        <div className="form-field">
          <label htmlFor={`${type}-pfiOrPoNumber`}>PFI / PO Number</label>
          <input
            id={`${type}-pfiOrPoNumber`}
            type="text"
            value={formState.pfiOrPoNumber}
            onChange={(event) => handleChange('pfiOrPoNumber', event.target.value)}
            placeholder="Enter PFI or PO number e.g. PFI-2025-001 or PO-INV-003"
            required
          />
          <small style={{ display: 'block', color: '#64748b', marginTop: '4px' }}>
            Use the supplier's PFI for imported shipments, or PO for local purchases. Example: PFI-2025-001 or PO-INV-003.
          </small>
        </div>
      )}

      <div className="form-field">
        <label htmlFor={`${type}-remarks`}>Remarks</label>
        <textarea
          id={`${type}-remarks`}
          rows={3}
          value={formState.remarks}
          onChange={(event) => handleChange('remarks', event.target.value)}
          placeholder="Optional notes"
        />
      </div>

      <div className="form-field">
        <label htmlFor={`${type}-countryOfOrigin`}>Country of Origin</label>
        <input
          id={`${type}-countryOfOrigin`}
          type="text"
          value={formState.countryOfOrigin}
          onChange={(event) => handleChange('countryOfOrigin', event.target.value)}
          placeholder="e.g. China, India, South Africa"
        />
      </div>

      {normalizedType === 'receive' && (
        <div className="form-field">
          <label htmlFor={`${type}-checkedBy`}>Checked By</label>
          <select
            id={`${type}-checkedBy`}
            value={formState.checkedBy}
            onChange={(event) => handleChange('checkedBy', event.target.value)}
          >
            <option value="">Select user</option>
            {usersLoading ? (
              <option disabled>Loading users...</option>
            ) : (
              filteredUsersForCheckedBy.map((user) => (
                <option key={user._id || user.id} value={user._id || user.id}>
                  {user.name || user.email}
                </option>
              ))
            )}
          </select>
        </div>
      )}

      {normalizedType === 'receive' && (
        <div className="form-field">
          <label>Attachments (Optional)</label>
          <small style={{ display: 'block', color: '#64748b', marginBottom: '8px' }}>
            Upload COA, Packing List, BL/AWBL, Invoice, etc. (PDF, JPEG, PNG, DOCX)
          </small>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              onChange={async (e) => {
                const newFiles = Array.from(e.target.files || []);
                if (newFiles.length > 0) {
                  setUploadingFiles(true);
                  try {
                    const uploadedFiles = await uploadFilesWithAutoChunking({
                      files: newFiles,
                      context: 'documents'
                    });
                    setAttachments(prev => [...prev, ...uploadedFiles]);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    // Clear any previous upload errors on success
                    if (onClearError) {
                      onClearError();
                    }
                  } catch (error) {
                    console.error('File upload error:', error);
                    let errorMessage = error.message || 'Failed to upload files';
                    
                    // Provide more helpful messages for rate limit errors
                    if (errorMessage.includes('rate limit') || errorMessage.includes('RATE_LIMIT')) {
                      errorMessage = 'Upload rate limit exceeded. The server allows 20 uploads per 5 minutes. Please wait 5 minutes before trying again, or try uploading a smaller file.';
                    }
                    
                    // Set error using the callback prop
                    if (onSetError) {
                      onSetError(`File upload failed: ${errorMessage}`);
                    }
                    // Also show alert for immediate feedback
                    alert(`Failed to upload files: ${errorMessage}`);
                  } finally {
                    setUploadingFiles(false);
                  }
                }
              }}
              disabled={uploadingFiles}
              style={{ display: 'none' }}
              id={`${type}-file-input`}
            />
            <label
              htmlFor={`${type}-file-input`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                border: '1px solid #cbd5e1',
                borderRadius: '4px',
                cursor: uploadingFiles ? 'not-allowed' : 'pointer',
                backgroundColor: uploadingFiles ? '#f1f5f9' : 'white',
                color: uploadingFiles ? '#94a3b8' : '#334155'
              }}
            >
              <Upload size={16} />
              {uploadingFiles ? 'Uploading...' : 'Upload Files'}
            </label>
            {attachments.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
                {attachments.map((file, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', backgroundColor: '#f8fafc', borderRadius: '4px' }}>
                    <FileText size={14} />
                    <span style={{ flex: 1, fontSize: '0.875rem' }}>{file.originalName || file.filename}</span>
                    <button
                      type="button"
                      onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {normalizedType === 'issue' && (
        <>
          <div className="form-field">
            <label htmlFor={`${type}-issueTo`}>Issue to</label>
            <input
              id={`${type}-issueTo`}
              type="text"
              value={formState.issueTo}
              onChange={(event) => handleChange('issueTo', event.target.value)}
              placeholder="e.g. Production Line 1, Department A"
              required
            />
          </div>
          <div className="form-field">
            <label htmlFor={`${type}-reason`}>Reason</label>
            <select
              id={`${type}-reason`}
              value={formState.reason}
              onChange={(event) => handleChange('reason', event.target.value)}
              required
            >
              <option value="">Select a reason</option>
              <option value="Consumption">Consumption</option>
              <option value="Damage">Damage</option>
              <option value="Transfer">Transfer</option>
              <option value="Adjustment">Adjustment</option>
              <option value="Other">Other</option>
            </select>
          </div>
          {formState.reason === 'Other' && (
            <div className="form-field">
              <label htmlFor={`${type}-otherReason`}>Specify other reason</label>
              <input
                id={`${type}-otherReason`}
                type="text"
                value={formState.otherReason}
                onChange={(event) => handleChange('otherReason', event.target.value)}
                placeholder="e.g. Production Line 1, Department A"
              />
            </div>
          )}
        </>
      )}

      {normalizedType === 'adjust' && (
        <>
          <div className="form-field">
            <label htmlFor={`${type}-newQuantity`}>New quantity (kg)</label>
            <input
              id={`${type}-newQuantity`}
              type="number"
              min="0"
              step="any"
              value={formState.newQuantity}
              onChange={(event) => handleNumberChange('newQuantity', event.target.value)}
              placeholder="e.g. 100"
              required
            />
          </div>
          <div className="form-field">
            <label htmlFor={`${type}-reason`}>Reason</label>
            <select
              id={`${type}-reason`}
              value={formState.reason}
              onChange={(event) => handleChange('reason', event.target.value)}
              required
            >
              <option value="">Select a reason</option>
              <option value="Consumption">Consumption</option>
              <option value="Damage">Damage</option>
              <option value="Transfer">Transfer</option>
              <option value="Adjustment">Adjustment</option>
              <option value="Other">Other</option>
            </select>
          </div>
          {formState.reason === 'Other' && (
            <div className="form-field">
              <label htmlFor={`${type}-otherReason`}>Specify other reason</label>
              <input
                id={`${type}-otherReason`}
                type="text"
                value={formState.otherReason}
                onChange={(event) => handleChange('otherReason', event.target.value)}
                placeholder="e.g. Production Line 1, Department A"
              />
            </div>
          )}
        </>
      )}
    </>
  );

  const renderIssueFields = () => (
    <>
      {context.order && (
        <div className="order-detail-grid">
          <div>
            <span>Inv / Order no.</span>
            <strong>{context.order.orderNo || '—'}</strong>
          </div>
          <div>
            <span>Product</span>
            <strong>{context.order.product?.productName ?? context.order.productName ?? '—'}</strong>
          </div>
          <div>
            <span>Quantity ordered</span>
            <strong>
              {formatNumber(context.order.qtyOrdered ?? formState.qty ?? 0)}{' '}
              {context.order.unit ?? formState.productUnit ?? 'kg'}
            </strong>
          </div>
          <div>
            <span>ETD</span>
            <strong>{formatDate(context.order.etd)}</strong>
          </div>
        </div>
      )}

      <div className="form-field">
        <label htmlFor={`${type}-product`}>Product</label>
        <select
          id={`${type}-product`}
          value={productSelection}
          onChange={(event) => handleProductSelectionChange(event.target.value)}
          required
        >
          <option value="" disabled>
            Select product
          </option>
          {inventoryOptionList.length > 0 && (
            <optgroup label="Inventory products">
              {inventoryOptionList.map((product) => (
                <option key={product.id} value={`inventory:${product.id}`}>
                  {product.productName}
                </option>
              ))}
            </optgroup>
          )}
          {normalizedType === 'issue' && costCardProducts.length > 0 && (
            <optgroup label="Cost card products">
              {costCardProducts.map((product) => (
                <option
                  key={`costcard:${product.productName}`}
                  value={`costcard:${encodeCostCardKey(product.productName)}`}
                >
                  {product.productName}
                </option>
              ))}
            </optgroup>
          )}
          {normalizedType === 'issue' && (
            <optgroup label="Manual entry">
              <option value="new:new">Create new product…</option>
            </optgroup>
          )}
        </select>
      </div>

      {normalizedType === 'issue' && issueProducts.length > 1 && (
        <div className="form-field wide">
          <div className="issue-product-list">
            <table className="issue-product-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>HS code</th>
                  <th>Available qty (kg)</th>
                  <th>Batches</th>
                </tr>
              </thead>
              <tbody>
                {issueProducts.map((product) => {
                  const isSelected =
                    formState.productId &&
                    String(formState.productId) === String(product.productId);
                  return (
                    <tr
                      key={product.productId}
                      className={`issue-product-row ${isSelected ? 'selected' : ''}`}
                      onClick={() =>
                        handleProductSelectionChange(`inventory:${product.productId}`)
                      }
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleProductSelectionChange(`inventory:${product.productId}`);
                        }
                      }}
                    >
                      <td>{product.productName}</td>
                      <td>{product.hsCode || '—'}</td>
                      <td>{formatNumber(product.totalQty ?? 0)}</td>
                      <td>
                        {product.batches && product.batches.length > 0
                          ? product.batches
                              .map((batch) =>
                                `${batch.batchNo} (${formatNumber(batch.currentQtyKg ?? 0)} kg)`
                              )
                              .join(', ')
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="form-field">
        <label htmlFor={`${type}-batchId`}>Batch</label>
        {recommended && normalizedType === 'issue' && (
          <div style={{ marginBottom: '4px', fontSize: '0.75rem', color: '#059669', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>✓ Recommended batch based on earliest expiry date (FEFO)</span>
          </div>
        )}
        <select
          id={`${type}-batchId`}
          value={formState.batchId}
          onChange={(event) => handleChange('batchId', event.target.value)}
          required
        >
          <option value="">Select a batch</option>
          {filteredBatches.map((batch) => {
            const expiryDate = batch.expiryDate ? formatDate(batch.expiryDate) : 'N/A';
            const qty = formatNumber(batch.currentQtyKg ?? 0);
            const isRecommended = recommended && normalizedType === 'issue' && (batch.id ?? batch._id) === (recommended.id ?? recommended._id);
            return (
              <option key={batch.id ?? batch._id} value={batch.id ?? batch._id} style={isRecommended ? { backgroundColor: '#d1fae5' } : {}}>
                {batch.batchNo} — {expiryDate} — {qty}kg
              </option>
            );
          })}
        </select>
      </div>

      <div className="form-field">
        <label htmlFor={`${type}-qty`}>Quantity (kg)</label>
        <input
          id={`${type}-qty`}
          type="number"
          min="0"
          step="any"
          value={formState.qty}
          onChange={(event) => handleNumberChange('qty', event.target.value)}
          placeholder="e.g. 100"
          required
        />
      </div>

      {normalizedType === 'issue' && (
        <div className="form-field">
          <label htmlFor={`${type}-poNumber`}>PO Number</label>
          <input
            id={`${type}-poNumber`}
            type="text"
            value={formState.poNumber}
            onChange={(event) => handleChange('poNumber', event.target.value)}
            placeholder="e.g. PO-INV-001"
          />
        </div>
      )}

      {normalizedType === 'issue' && (
        <div className="form-field">
          <label htmlFor={`${type}-customer`}>Customer</label>
          <input
            id={`${type}-customer`}
            type="text"
            value={formState.customer}
            onChange={(event) => handleChange('customer', event.target.value)}
            placeholder="e.g. Customer name"
          />
        </div>
      )}

      {normalizedType === 'issue' && (
        <div className="form-field">
          <label htmlFor={`${type}-checkedBy`}>Checked By</label>
          <select
            id={`${type}-checkedBy`}
            value={formState.checkedBy}
            onChange={(event) => handleChange('checkedBy', event.target.value)}
          >
            <option value="">Select user</option>
            {usersLoading ? (
              <option disabled>Loading users...</option>
            ) : (
              filteredUsersForCheckedBy.map((user) => (
                <option key={user._id || user.id} value={user._id || user.id}>
                  {user.name || user.email}
                </option>
              ))
            )}
          </select>
        </div>
      )}

      {normalizedType === 'issue' && (
        <div className="form-field">
          <label>Attachments (Optional)</label>
          <small style={{ display: 'block', color: '#64748b', marginBottom: '8px' }}>
            Upload Delivery Note, Dispatch Sheets, Customer Acknowledgment, Transport Documents, etc. (PDF, JPEG, PNG, DOCX)
          </small>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              onChange={async (e) => {
                const newFiles = Array.from(e.target.files || []);
                if (newFiles.length > 0) {
                  setUploadingFiles(true);
                  try {
                    const uploadedFiles = await uploadFilesWithAutoChunking({
                      files: newFiles,
                      context: 'documents'
                    });
                    setAttachments(prev => [...prev, ...uploadedFiles]);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    // Clear any previous upload errors on success
                    if (onClearError) {
                      onClearError();
                    }
                  } catch (error) {
                    console.error('File upload error:', error);
                    let errorMessage = error.message || 'Failed to upload files';
                    
                    // Provide more helpful messages for rate limit errors
                    if (errorMessage.includes('rate limit') || errorMessage.includes('RATE_LIMIT')) {
                      errorMessage = 'Upload rate limit exceeded. The server allows 20 uploads per 5 minutes. Please wait 5 minutes before trying again, or try uploading a smaller file.';
                    }
                    
                    // Set error using the callback prop
                    if (onSetError) {
                      onSetError(`File upload failed: ${errorMessage}`);
                    }
                    // Also show alert for immediate feedback
                    alert(`Failed to upload files: ${errorMessage}`);
                  } finally {
                    setUploadingFiles(false);
                  }
                }
              }}
              disabled={uploadingFiles}
              style={{ display: 'none' }}
              id={`${type}-issue-file-input`}
            />
            <label
              htmlFor={`${type}-issue-file-input`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                border: '1px solid #cbd5e1',
                borderRadius: '4px',
                cursor: uploadingFiles ? 'not-allowed' : 'pointer',
                backgroundColor: uploadingFiles ? '#f1f5f9' : 'white',
                color: uploadingFiles ? '#94a3b8' : '#334155'
              }}
            >
              <Upload size={16} />
              {uploadingFiles ? 'Uploading...' : 'Upload Files'}
            </label>
            {attachments.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
                {attachments.map((file, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', backgroundColor: '#f8fafc', borderRadius: '4px' }}>
                    <FileText size={14} />
                    <span style={{ flex: 1, fontSize: '0.875rem' }}>{file.originalName || file.filename}</span>
                    <button
                      type="button"
                      onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="form-field">
        <label htmlFor={`${type}-remarks`}>Remarks</label>
        <textarea
          id={`${type}-remarks`}
          rows={3}
          value={formState.remarks}
          onChange={(event) => handleChange('remarks', event.target.value)}
          placeholder="Optional notes"
        />
      </div>
    </>
  );

  const renderAdjustFields = () => (
    <>
      {context.order && (
        <div className="order-detail-grid">
          <div>
            <span>Inv / Order no.</span>
            <strong>{context.order.orderNo || '—'}</strong>
          </div>
          <div>
            <span>Product</span>
            <strong>{context.order.product?.productName ?? context.order.productName ?? '—'}</strong>
          </div>
          <div>
            <span>Quantity ordered</span>
            <strong>
              {formatNumber(context.order.qtyOrdered ?? formState.qty ?? 0)}{' '}
              {context.order.unit ?? formState.productUnit ?? 'kg'}
            </strong>
          </div>
          <div>
            <span>ETD</span>
            <strong>{formatDate(context.order.etd)}</strong>
          </div>
        </div>
      )}

      <div className="form-field">
        <label htmlFor={`${type}-product`}>Product</label>
        <select
          id={`${type}-product`}
          value={productSelection}
          onChange={(event) => handleProductSelectionChange(event.target.value)}
          required
        >
          <option value="" disabled>
            Select product
          </option>
          {inventoryOptionList.length > 0 && (
            <optgroup label="Inventory products">
              {inventoryOptionList.map((product) => (
                <option key={product.id} value={`inventory:${product.id}`}>
                  {product.productName}
                </option>
              ))}
            </optgroup>
          )}
          {normalizedType === 'adjust' && costCardProducts.length > 0 && (
            <optgroup label="Cost card products">
              {costCardProducts.map((product) => (
                <option
                  key={`costcard:${product.productName}`}
                  value={`costcard:${encodeCostCardKey(product.productName)}`}
                >
                  {product.productName}
                </option>
              ))}
            </optgroup>
          )}
          {normalizedType === 'adjust' && (
            <optgroup label="Manual entry">
              <option value="new:new">Create new product…</option>
            </optgroup>
          )}
        </select>
      </div>

      {normalizedType === 'adjust' && issueProducts.length > 1 && (
        <div className="form-field wide">
          <div className="issue-product-list">
            <table className="issue-product-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>HS code</th>
                  <th>Available qty (kg)</th>
                  <th>Batches</th>
                </tr>
              </thead>
              <tbody>
                {issueProducts.map((product) => {
                  const isSelected =
                    formState.productId &&
                    String(formState.productId) === String(product.productId);
                  return (
                    <tr
                      key={product.productId}
                      className={`issue-product-row ${isSelected ? 'selected' : ''}`}
                      onClick={() =>
                        handleProductSelectionChange(`inventory:${product.productId}`)
                      }
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleProductSelectionChange(`inventory:${product.productId}`);
                        }
                      }}
                    >
                      <td>{product.productName}</td>
                      <td>{product.hsCode || '—'}</td>
                      <td>{formatNumber(product.totalQty ?? 0)}</td>
                      <td>
                        {product.batches && product.batches.length > 0
                          ? product.batches
                              .map((batch) =>
                                `${batch.batchNo} (${formatNumber(batch.currentQtyKg ?? 0)} kg)`
                              )
                              .join(', ')
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="form-field">
        <label htmlFor={`${type}-batchId`}>Batch ID</label>
        <select
          id={`${type}-batchId`}
          value={formState.batchId}
          onChange={(event) => handleChange('batchId', event.target.value)}
          required
        >
          <option value="">Select a batch</option>
          {filteredBatches.map((batch) => (
            <option key={batch.id ?? batch._id} value={batch.id ?? batch._id}>
              {batch.productName} - {batch.batchNo} ({formatNumber(batch.currentQtyKg ?? 0)} kg)
            </option>
          ))}
        </select>
      </div>

      <div className="form-field">
        <label htmlFor={`${type}-newQuantity`}>New quantity (kg)</label>
        <input
          id={`${type}-newQuantity`}
          type="number"
          min="0"
          value={formState.newQuantity}
          onChange={(event) => handleNumberChange('newQuantity', event.target.value)}
          placeholder="e.g. 100"
          required
        />
      </div>

      <div className="form-field">
        <label htmlFor={`${type}-reason`}>Reason</label>
        <select
          id={`${type}-reason`}
          value={formState.reason}
          onChange={(event) => handleChange('reason', event.target.value)}
          required
        >
          <option value="">Select a reason</option>
          <option value="Consumption">Consumption</option>
          <option value="Damage">Damage</option>
          <option value="Used as a tester">Used as a tester</option>
          <option value="Other">Other</option>
        </select>
      </div>
      {formState.reason === 'Other' && (
        <div className="form-field">
          <label htmlFor={`${type}-otherReason`}>Specify other reason</label>
          <input
            id={`${type}-otherReason`}
            type="text"
            value={formState.otherReason}
            onChange={(event) => handleChange('otherReason', event.target.value)}
            placeholder="e.g. Production Line 1, Department A"
          />
        </div>
      )}

      <div className="form-field">
        <label htmlFor={`${type}-remarks`}>Remarks</label>
        <textarea
          id={`${type}-remarks`}
          rows={3}
          value={formState.remarks}
          onChange={(event) => handleChange('remarks', event.target.value)}
          placeholder="Optional notes"
        />
      </div>

      {normalizedType === 'adjust' && (
      <div className="form-field">
          <label htmlFor={`${type}-checkedBy`}>Checked By</label>
          <select
            id={`${type}-checkedBy`}
            value={formState.checkedBy}
            onChange={(event) => handleChange('checkedBy', event.target.value)}
          >
            <option value="">Select user</option>
            {usersLoading ? (
              <option disabled>Loading users...</option>
            ) : (
              filteredUsersForCheckedBy.map((user) => (
                <option key={user._id || user.id} value={user._id || user.id}>
                  {user.name || user.email}
                </option>
              ))
            )}
          </select>
        </div>
      )}

      {normalizedType === 'adjust' && (
        <div className="form-field">
          <label>Attachments (Optional)</label>
          <small style={{ display: 'block', color: '#64748b', marginBottom: '8px' }}>
            Upload supporting documents for adjustment (PDF, JPEG, PNG, DOCX)
          </small>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              onChange={async (e) => {
                const newFiles = Array.from(e.target.files || []);
                if (newFiles.length > 0) {
                  setUploadingFiles(true);
                  try {
                    const uploadedFiles = await uploadFilesWithAutoChunking({
                      files: newFiles,
                      context: 'documents'
                    });
                    setAttachments(prev => [...prev, ...uploadedFiles]);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    // Clear any previous upload errors on success
                    if (onClearError) {
                      onClearError();
                    }
                  } catch (error) {
                    console.error('File upload error:', error);
                    let errorMessage = error.message || 'Failed to upload files';
                    
                    // Provide more helpful messages for rate limit errors
                    if (errorMessage.includes('rate limit') || errorMessage.includes('RATE_LIMIT')) {
                      errorMessage = 'Upload rate limit exceeded. The server allows 20 uploads per 5 minutes. Please wait 5 minutes before trying again, or try uploading a smaller file.';
                    }
                    
                    // Set error using the callback prop
                    if (onSetError) {
                      onSetError(`File upload failed: ${errorMessage}`);
                    }
                    // Also show alert for immediate feedback
                    alert(`Failed to upload files: ${errorMessage}`);
                  } finally {
                    setUploadingFiles(false);
                  }
                }
              }}
              disabled={uploadingFiles}
              style={{ display: 'none' }}
              id={`${type}-adjust-file-input`}
            />
            <label
              htmlFor={`${type}-adjust-file-input`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                border: '1px solid #cbd5e1',
                borderRadius: '4px',
                cursor: uploadingFiles ? 'not-allowed' : 'pointer',
                backgroundColor: uploadingFiles ? '#f1f5f9' : 'white',
                color: uploadingFiles ? '#94a3b8' : '#334155'
              }}
            >
              <Upload size={16} />
              {uploadingFiles ? 'Uploading...' : 'Upload Files'}
            </label>
            {attachments.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
                {attachments.map((file, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', backgroundColor: '#f8fafc', borderRadius: '4px' }}>
                    <FileText size={14} />
                    <span style={{ flex: 1, fontSize: '0.875rem' }}>{file.originalName || file.filename}</span>
                    <button
                      type="button"
                      onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                    >
                      <X size={14} />
                    </button>
      </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );

  const renderForm = () => {
    switch (type) {
      case 'receive-stock':
        return renderReceiveFields();
      case 'issue':
        return renderIssueFields();
      case 'adjust':
        return renderAdjustFields();
      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="modal-panel">
        <header className="modal-header">
          <h3>{titleMap[type]}</h3>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="modal-body">
          {localError && <p className="form-error">{localError}</p>}
          <form onSubmit={(event) => handleSubmit(event, type)}>
            {renderForm()}
            <footer className="modal-footer">
              <button
                type="button"
                className="action-btn secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="action-btn"
                disabled={submitting}
              >
                {submitting ? 'Processing…' : titleMap[type]}
              </button>
            </footer>
          </form>
        </div>
        {errorMessage && (
          <div className="modal-error-message">
            <AlertTriangle size={20} aria-hidden="true" />
            <p>{errorMessage}</p>
            {errorSuggestion && (
              <button
                type="button"
                className="action-btn secondary"
                onClick={applyBatchSuggestion}
              >
                {errorSuggestion}
              </button>
            )}
            <button
              type="button"
              className="action-btn"
              onClick={onClearError}
            >
              Dismiss
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryManagement;